import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatTime, formatDateTime } from "@/lib/datetime";
import { toast } from "sonner";
import {
    WhatsappLogo, PaperPlaneTilt, ChatCircleDots, GearSix, Key, ArrowClockwise,
    Plugs, CheckCircle, Copy, MagnifyingGlass, User, Paperclip, FilePdf, FileText,
    Trash, X, CircleNotch, Check, Checks, FileArrowDown, Cards, PlusCircle,
    Code, Eye, Buildings, Envelope, Robot, ListBullets, ArrowUp, ArrowDown, Info, Megaphone, Prohibit, FlowArrow,
    SidebarSimple, ArrowSquareOut, Clock, ArrowCounterClockwise, ArrowBendUpLeft,
} from "@phosphor-icons/react";
import WhatsAppBroadcasts from "./WhatsAppBroadcasts";
import WhatsAppJourneys from "./WhatsAppJourneys";
import WhatsAppOptouts from "./WhatsAppOptouts";

const TABS_ADMIN = [
    { key: "inbox", label: "Inbox", icon: ChatCircleDots },
    { key: "broadcasts", label: "Broadcasts", icon: Megaphone },
    { key: "journeys", label: "Journeys", icon: FlowArrow },
    { key: "templates", label: "Templates", icon: Cards },
    { key: "bot", label: "Bot", icon: Robot },
    { key: "optouts", label: "Opt-outs", icon: Prohibit },
    { key: "api", label: "Salesforce API", icon: Key },
];
const TABS_AGENT = [
    { key: "inbox", label: "Inbox", icon: ChatCircleDots },
    { key: "templates", label: "Templates", icon: Cards },
];

const initials = (n) => {
    const s = (n || "").trim();
    if (/[a-zA-Z]/.test(s)) {
        const p = s.split(/\s+/).filter(Boolean);
        return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "WA";
    }
    return s.replace(/\D/g, "").slice(-2) || "WA";
};

const AVATAR_COLORS = [
    "bg-rose-100 text-rose-700", "bg-fuchsia-100 text-fuchsia-700", "bg-violet-100 text-violet-700",
    "bg-sky-100 text-sky-700", "bg-amber-100 text-amber-700", "bg-teal-100 text-teal-700",
    "bg-indigo-100 text-indigo-700", "bg-lime-100 text-lime-700",
];
const avatarColor = (key) => {
    const s = (key || "").toString();
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
function Avatar({ label, colorKey, size = "h-10 w-10", text = "text-xs", dot = false }) {
    return (
        <div className={`relative grid ${size} shrink-0 place-items-center rounded-full font-bold ${text} ${avatarColor(colorKey || label)}`}>
            {initials(label)}
            {dot && <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500" />}
        </div>
    );
}

function DetailRow({ icon: Icon, label, value }) {
    if (!value) return null;
    return (
        <div className="flex items-start justify-between gap-3">
            <span className="flex shrink-0 items-center gap-1.5 text-stone2">{Icon && <Icon size={13} />} {label}</span>
            <span className="min-w-0 break-words text-right text-xs font-medium text-ink">{value}</span>
        </div>
    );
}

function FieldInput({ testid, label, value, onChange }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">{label}</span>
            <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
                className="rounded-lg border border-stone-300 px-2.5 py-1.5 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
        </label>
    );
}

function CopyField({ value, testid }) {
    return (
        <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 font-mono text-xs" data-testid={`${testid}-value`}>
                {value || "—"}
            </code>
            <button
                data-testid={`${testid}-copy`}
                onClick={() => { navigator.clipboard?.writeText(value || ""); toast.success("Copied"); }}
                className="rounded-lg border border-stone-300 p-2 text-stone2 transition-all duration-200 hover:border-ink hover:text-ink"
                title="Copy"
            >
                <Copy size={15} />
            </button>
        </div>
    );
}

function StatusTicks({ status }) {
    if (status === "read") return <Checks size={13} className="text-sky-500" weight="bold" />;
    if (status === "delivered") return <Checks size={13} className="text-stone-400" weight="bold" />;
    if (status === "failed") return <span className="font-semibold text-red-500">failed</span>;
    if (status === "sent") return <Check size={13} className="text-stone-400" weight="bold" />;
    return <span className="text-stone-400">{status}</span>;
}

/* Humanize a raw template slug: iw_interest_capture -> "Interest Capture" */
function humanizeTpl(name) {
    return String(name || "").replace(/^iw[_-]/i, "").replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}

/* Short one-line preview for the conversation list (no raw placeholder strings). */
function previewText(body) {
    const meta = parsePlaceholder(body);
    if (!meta) return body;
    if (meta.kind === "journey") return `${humanizeTpl(meta.name)} · Step ${meta.step}`;
    if (meta.kind === "template") return humanizeTpl(meta.name);
    if (meta.kind === "button") return "↩ Quick reply";
    return body;
}

/* Parse the server placeholder bodies into structured meta so the thread never shows raw
   "[journey: X · step N]" / "[button]" / "[template: X]" strings. */
function parsePlaceholder(body) {
    if (!body) return null;
    const j = body.match(/^\[journey:\s*(.+?)\s*·\s*step\s*(\d+)\]$/i);
    if (j) return { kind: "journey", name: j[1], step: Number(j[2]) };
    const t = body.match(/^\[template:\s*(.+?)\]$/i);
    if (t) return { kind: "template", name: t[1] };
    if (/^\[button\]$/i.test(body.trim())) return { kind: "button" };
    return null;
}

/* Enterprise-grade rendering of a single message body (text / template card / media). */
function MessageBody({ m, onDelete }) {
    const meta = parsePlaceholder(m.body);
    // Inbound quick-reply / button tap (logged as type "button"/"interactive" or body "[button]").
    if ((meta && meta.kind === "button") || m.type === "button" || m.type === "interactive" || m.type === "button_reply") {
        const label = m.button_payload || m.interactive_id || m.body_label ||
            (m.body && !parsePlaceholder(m.body) ? m.body : "") || "Quick reply";
        return (
            <span data-testid="wa-msg-button-chip" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600/10 px-3 py-1 text-[13px] font-semibold text-emerald-700">
                <ArrowBendUpLeft size={13} weight="bold" /> {label}
            </span>
        );
    }
    if (m.type !== "text" && m.type !== "template") return <MediaContent m={m} onDelete={onDelete} />;
    const tplName = m.template_name || (meta && meta.name);
    // Automated template message (journey step or one-off template) with no stored copy.
    if ((m.type === "template" || (meta && (meta.kind === "journey" || meta.kind === "template"))) && (!m.body || meta)) {
        return (
            <div data-testid="wa-msg-template-card" className="min-w-[220px]">
                <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-600/10 text-emerald-700">
                        <PaperPlaneTilt size={14} weight="fill" />
                    </span>
                    <div className="leading-tight">
                        <div className="text-[13px] font-semibold text-ink">{humanizeTpl(tplName) || "Template message"}</div>
                        <div className="text-[10px] font-medium uppercase tracking-wider text-emerald-700/70">Approved template</div>
                    </div>
                </div>
                {meta && meta.kind === "journey" && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-black/5 pt-2 text-[10.5px] text-stone-500">
                        <span className="rounded-full bg-black/5 px-2 py-0.5 font-semibold text-stone-600">Journey</span>
                        <span className="max-w-[180px] truncate">{meta.name}</span>
                        <span className="rounded-full bg-emerald-600/10 px-2 py-0.5 font-semibold text-emerald-700">Step {meta.step}</span>
                    </div>
                )}
            </div>
        );
    }
    return <div className="whitespace-pre-wrap break-words">{m.body}</div>;
}


/* ---------------- Media bubble ---------------- */
function MediaContent({ m, onDelete }) {
    const url = m.media_ref ? `${API_BASE}/whatsapp/media/${m.media_ref}` : null;
    if (m.media_deleted || (!url && m.type !== "text" && m.type !== "template")) {
        return <div className="text-xs italic text-stone-400">{m.media_deleted ? "media deleted" : `[${m.type}]`}</div>;
    }
    if (!url) return null;
    const isImage = (m.media_mime || "").startsWith("image/") || m.type === "image";
    return (
        <div className="group/media relative">
            {isImage ? (
                <a href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt={m.filename || "image"} className="max-w-[240px] rounded-lg" data-testid={`wa-media-img-${m.id}`} />
                </a>
            ) : (
                <a href={url} target="_blank" rel="noreferrer"
                   className="flex items-center gap-3 rounded-lg border border-black/5 bg-white/70 p-2.5" data-testid={`wa-media-doc-${m.id}`}>
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-red-50 text-red-500">
                        <FilePdf size={20} weight="fill" />
                    </div>
                    <div className="min-w-0">
                        <div className="truncate text-xs font-semibold text-ink">{m.filename || "Document"}</div>
                        <div className="text-[10px] text-stone2">Tap to open</div>
                    </div>
                    <FileArrowDown size={16} className="ml-2 text-stone-400" />
                </a>
            )}
            <button
                data-testid={`wa-media-delete-${m.id}`}
                onClick={() => onDelete(m.media_ref)}
                title="Delete media"
                className="absolute right-1.5 top-1.5 hidden rounded-full bg-black/60 p-1.5 text-white transition-all group-hover/media:block hover:bg-red-600"
            >
                <Trash size={12} weight="fill" />
            </button>
            {m.caption && <div className="mt-1.5 whitespace-pre-wrap break-words text-sm">{m.caption}</div>}
        </div>
    );
}

/* ---------------- Inbox ---------------- */
function Inbox() {
    const { orgTimezone, user } = useAuth();
    const canAssign = user?.role === "admin" || user?.role === "supervisor";
    const [convos, setConvos] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [q, setQ] = useState("");
    const [seg, setSeg] = useState("all");
    const [counts, setCounts] = useState({ all: 0, unread: 0, needs_agent: 0, assigned_me: 0 });
    const [agents, setAgents] = useState([]);
    const [assigning, setAssigning] = useState(false);
    const [panelOpen, setPanelOpen] = useState(true);
    const [info, setInfo] = useState(null);
    const [optBusy, setOptBusy] = useState(false);
    const [autoAssign, setAutoAssign] = useState(false);
    const [editC, setEditC] = useState(false);
    const [cForm, setCForm] = useState({ name: "", email: "", city: "", tags: "", attrs: [] });
    const [savingC, setSavingC] = useState(false);
    const [newTo, setNewTo] = useState("");
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const threadRef = useRef(null);
    const fileRef = useRef(null);

    const scrollDown = () => setTimeout(() => { threadRef.current && (threadRef.current.scrollTop = threadRef.current.scrollHeight); }, 60);

    const apiConvos = async (params) => {
        const { data } = await api.get("/whatsapp/conversations", { params: { seg, ...params } });
        return { items: data.items || [], next: data.next_cursor || null, more: !!data.has_more };
    };
    const loadCounts = async () => {
        try { const { data } = await api.get("/whatsapp/conversation-counts"); setCounts(data || {}); }
        catch (_e) { /* gated */ }
    };
    // First page (or a fresh search / segment change). Server-side search by name/number.
    const loadConvos = async (search = q) => {
        setLoading(true);
        try {
            const r = await apiConvos((search || "").trim() ? { q: search.trim() } : {});
            setConvos(r.items); setCursor(r.next); setHasMore(r.more);
        } catch (_e) { /* gated */ } finally { setLoading(false); }
    };
    // Infinite scroll — append the next page (disabled while searching).
    const loadMore = async () => {
        if (loadingMore || !hasMore || (q || "").trim()) return;
        setLoadingMore(true);
        try { const r = await apiConvos({ cursor }); setConvos((p) => [...p, ...r.items]); setCursor(r.next); setHasMore(r.more); }
        catch (_e) { /* ignore */ } finally { setLoadingMore(false); }
    };
    // Poll page-1 and merge in place so recent chats + unread refresh without losing scroll.
    const refreshTop = async () => {
        try {
            const r = await apiConvos((q || "").trim() ? { q: q.trim() } : {});
            setConvos((prev) => {
                const map = new Map(prev.map((c) => [c.contact, c]));
                r.items.forEach((c) => map.set(c.contact, c));
                return Array.from(map.values()).sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));
            });
        } catch (_e) { /* ignore */ }
        loadCounts();
    };
    const loadThread = async (contact) => {
        if (!contact) return;
        try { const { data } = await api.get(`/whatsapp/conversations/${contact}/messages`); setMessages(data); scrollDown(); }
        catch (_e) { /* ignore */ }
    };
    const loadInfo = async (contact) => {
        if (!contact) return;
        try { const { data } = await api.get(`/whatsapp/conversations/${contact}/info`); setInfo(data); }
        catch (_e) { setInfo(null); }
    };

    // Load agents (for the assignment dropdown) + initial counts once on mount.
    useEffect(() => {
        loadCounts();
        api.get("/whatsapp/assignable-agents").then(({ data }) => setAgents(data || [])).catch(() => {});
        if (user?.role === "admin") {
            api.get("/whatsapp/auto-assign-config").then(({ data }) => setAutoAssign(!!data.enabled)).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Load on mount + debounced server-side search / segment switch.
    useEffect(() => {
        const t = setTimeout(() => loadConvos(q), q ? 350 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, seg]);
    useEffect(() => {
        if (!active) return;
        setEditC(false);
        loadThread(active);
        loadInfo(active);
        const t = setInterval(() => { loadThread(active); refreshTop(); }, 8000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    const toggleAutoAssign = async () => {
        const next = !autoAssign;
        setAutoAssign(next);
        try {
            await api.put("/whatsapp/auto-assign-config", { enabled: next });
            toast.success(next ? "Auto-assign on — escalated chats route to the next agent" : "Auto-assign off");
        } catch (e) { setAutoAssign(!next); toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const startEditContact = () => {
        setCForm({
            name: info?.contact_name || "", email: info?.email || "", city: info?.city || "",
            tags: (info?.tags || []).join(", "),
            attrs: Object.entries(info?.attributes || {}).map(([k, v]) => ({ k, v: String(v) })),
        });
        setEditC(true);
    };
    const saveContact = async () => {
        setSavingC(true);
        try {
            const attributes = {};
            cForm.attrs.forEach((a) => { if ((a.k || "").trim()) attributes[a.k.trim()] = a.v; });
            await api.put(`/whatsapp/conversations/${active}/contact`, {
                name: cForm.name, email: cForm.email, city: cForm.city,
                tags: cForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
                attributes,
            });
            await loadInfo(active); await refreshTop();
            setEditC(false);
            toast.success("Contact updated");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSavingC(false); }
    };

    const assign = async (val) => {
        if (!active) return;
        setAssigning(true);
        try {
            await api.post(`/whatsapp/conversations/${active}/assign`, { assigned_to: val || null });
            setConvos((prev) => prev.map((c) => c.contact === active
                ? { ...c, assigned_to: val || null, assigned_to_name: val ? (agents.find((a) => a.id === val)?.name || agents.find((a) => a.id === val)?.email) : null }
                : c));
            await loadCounts();
            await loadInfo(active);
            toast.success(val ? "Conversation assigned" : "Conversation unassigned");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setAssigning(false); }
    };

    const toggleOptout = async () => {
        if (!active || !info) return;
        setOptBusy(true);
        const path = info.opted_out ? "/whatsapp/optouts/resubscribe" : "/whatsapp/optouts";
        try {
            await api.post(path, { contact: active });
            await loadInfo(active);
            toast.success(info.opted_out ? "Contact re-subscribed" : "Contact opted out");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setOptBusy(false); }
    };

    const onListScroll = (e) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 140) loadMore();
    };
    const displayName = (c) => c.contact_name || `+${c.contact}`;

    const normalize = (n) => { const d = (n || "").replace(/\D/g, ""); return d.length === 10 ? "91" + d : d; };

    const send = async () => {
        const to = active || newTo;
        if (!to || !text.trim()) return;
        setSending(true);
        try {
            await api.post("/whatsapp/send", { to, type: "text", body: text.trim() });
            setText("");
            if (!active) setActive(normalize(to));
            await loadThread(active || normalize(to));
            await refreshTop();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSending(false); }
    };

    const sendTemplate = async () => {
        const to = active || newTo;
        if (!to) { toast.error("Pick or start a chat first"); return; }
        setSending(true);
        try {
            await api.post("/whatsapp/send", { to, type: "template", template_name: "hello_world", language: "en_US" });
            toast.success("hello_world template sent — check the phone!");
            await loadThread(active || normalize(to)); await refreshTop();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSending(false); }
    };

    const onPickFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        const to = active || newTo;
        if (!file || !to) { toast.error("Open a chat before attaching"); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("to", to);
            fd.append("caption", text.trim());
            fd.append("file", file);
            await api.post("/whatsapp/send-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setText("");
            if (!active) setActive(normalize(to));
            await loadThread(active || normalize(to)); await refreshTop();
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
        finally { setUploading(false); }
    };

    const deleteMedia = async (ref) => {
        try { await api.delete(`/whatsapp/media/${ref}`); await loadThread(active); toast.success("Media deleted"); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    return (
        <div className="flex h-[calc(100vh-13rem)] min-h-[600px] w-full overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
            {/* Conversation list */}
            <div className="flex w-full max-w-[320px] shrink-0 flex-col border-r border-stone-200 bg-white">
                <div className="flex items-center justify-between px-4 pt-4">
                    <div>
                        <p className="font-heading text-sm font-bold text-ink">Inbox</p>
                        <p className="text-[11px] text-stone2">All your WhatsApp chats</p>
                    </div>
                    {user?.role === "admin" ? (
                        <button data-testid="wa-auto-assign-toggle" onClick={toggleAutoAssign}
                            title="Round-robin auto-assign escalated chats to the next available agent"
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors duration-200 ${autoAssign ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}>
                            <FlowArrow size={12} weight={autoAssign ? "fill" : "regular"} /> Auto {autoAssign ? "on" : "off"}
                        </button>
                    ) : (
                        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-semibold text-stone-600">{counts.all || 0}</span>
                    )}
                </div>
                <div className="flex flex-col gap-3 border-b border-stone-100 p-4 pt-3">
                    <div className="relative">
                        <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                        <input data-testid="wa-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or number…"
                            className="w-full rounded-full border border-stone-300 bg-white py-2 pl-9 pr-3 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                    </div>
                    <div className="flex items-center gap-2">
                        <input data-testid="wa-new-number" value={newTo} onChange={(e) => setNewTo(e.target.value)} placeholder="New number e.g. 9198…"
                            className="flex-1 rounded-full border border-stone-300 bg-white px-3 py-2 text-xs outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                        <button data-testid="wa-start-chat" onClick={() => { const d = normalize(newTo); if (d) { setActive(d); setNewTo(""); setMessages([]); } }}
                            className="inline-flex items-center gap-1 rounded-full bg-ink px-3 py-2 text-xs font-semibold text-white transition-all duration-200 hover:-translate-y-0.5">
                            <PlusCircle size={14} weight="fill" /> Start
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-1 border-b border-stone-100 px-2 py-2" data-testid="wa-seg-tabs">
                    {[
                        { k: "all", label: "All" },
                        { k: "unread", label: "Unread" },
                        { k: "needs_agent", label: "Needs agent" },
                        { k: "assigned_me", label: "Mine" },
                    ].map((s) => (
                        <button key={s.k} data-testid={`wa-seg-${s.k}`} onClick={() => setSeg(s.k)}
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors duration-200 ${seg === s.k ? "bg-emerald-600 text-white" : "text-stone2 hover:bg-stone-100"}`}>
                            {s.label}
                            {(counts[s.k] || 0) > 0 && (
                                <span className={`grid h-4 min-w-4 place-items-center rounded-full px-1 text-[9px] font-bold ${seg === s.k ? "bg-white/25 text-white" : "bg-stone-200 text-stone-600"}`}>{counts[s.k]}</span>
                            )}
                        </button>
                    ))}
                </div>
                <div className="flex-1 overflow-y-auto" data-testid="wa-convo-list" onScroll={onListScroll}>
                    {loading && <div className="p-6 text-center text-sm text-stone2">Loading…</div>}
                    {!loading && convos.length === 0 && (
                        <div className="flex flex-col items-center gap-2 p-8 text-center" data-testid="wa-convo-empty">
                            <ChatCircleDots size={30} className="text-stone-300" />
                            <p className="text-sm text-stone2">{q ? "No matches found." : seg === "unread" ? "No unread conversations." : seg === "needs_agent" ? "No conversations need an agent." : seg === "assigned_me" ? "Nothing assigned to you." : "No conversations yet."}</p>
                        </div>
                    )}
                    {convos.map((c) => (
                        <button key={c.contact} data-testid={`wa-convo-${c.contact}`} onClick={() => setActive(c.contact)}
                            className={`flex w-full items-center gap-3 border-l-2 px-4 py-3 text-left transition-colors duration-200 hover:bg-stone-50 ${active === c.contact ? "border-l-emerald-500 bg-emerald-50" : "border-l-transparent"}`}>
                            <Avatar label={c.contact_name || c.contact} colorKey={c.contact} dot={c.last_direction === "in"} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="flex min-w-0 items-center gap-1.5">
                                        <span className="truncate text-sm font-semibold text-ink">{displayName(c)}</span>
                                        {c.needs_agent && (
                                            <span data-testid={`wa-needs-agent-${c.contact}`} className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700">
                                                <User size={9} weight="fill" /> Needs agent
                                            </span>
                                        )}
                                    </span>
                                    <span className="shrink-0 text-[10px] text-stone-400">{c.timestamp ? formatTime(c.timestamp, orgTimezone) : ""}</span>
                                </div>
                                <div className="mt-0.5 flex items-center justify-between gap-2">
                                    <span className="truncate text-xs text-stone2">{c.contact_name ? `+${c.contact} · ` : ""}{c.last_direction === "out" ? "You: " : ""}{previewText(c.last_message)}</span>
                                    <div className="flex shrink-0 items-center gap-1.5">
                                        {c.assigned_to_name && (
                                            <span data-testid={`wa-assignee-${c.contact}`} title={`Assigned to ${c.assigned_to_name}`}
                                                className="inline-flex max-w-[84px] items-center gap-1 rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold text-sky-700">
                                                <User size={9} weight="fill" /><span className="truncate">{c.assigned_to_name}</span>
                                            </span>
                                        )}
                                        {c.unread > 0 && (
                                            <span data-testid={`wa-unread-${c.contact}`} className="grid h-[18px] min-w-[18px] place-items-center rounded-full bg-emerald-500 px-1 text-[10px] font-bold text-white">{c.unread}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </button>
                    ))}
                    {loadingMore && <div className="p-4 text-center text-xs text-stone2" data-testid="wa-convo-loadmore">Loading more…</div>}
                </div>
            </div>

            {/* Thread */}
            <div className="flex min-w-0 flex-1 flex-col bg-stone-50">
                {!active ? (
                    <div className="grid flex-1 place-items-center text-center">
                        <div>
                            <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-emerald-100">
                                <WhatsappLogo size={34} className="text-emerald-600" weight="fill" />
                            </div>
                            <p className="mt-4 font-heading text-lg font-bold text-ink">Your conversations</p>
                            <p className="mt-1 text-sm text-stone2">Select a chat or start a new one to begin messaging.</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-3 border-b border-stone-200 bg-white/90 px-5 py-3 backdrop-blur-md">
                            {(() => { const ac = convos.find((c) => c.contact === active); const nm = ac?.contact_name; return (<>
                            <Avatar label={nm || active} colorKey={active} />
                            <div className="min-w-0">
                                <div className="truncate font-semibold text-ink" data-testid="wa-active-contact">{nm || `+${active}`}</div>
                                <div className="flex items-center gap-1.5 text-[11px] text-stone2">
                                    {info?.window?.open
                                        ? <span className="inline-flex items-center gap-1 text-emerald-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active now</span>
                                        : <span className="inline-flex items-center gap-1 text-stone-400"><span className="h-1.5 w-1.5 rounded-full bg-stone-300" /> {nm ? `+${active}` : "WhatsApp"}</span>}
                                </div>
                            </div>
                            </>); })()}
                            <button data-testid="wa-send-template" onClick={sendTemplate} disabled={sending}
                                title="Send the pre-approved hello_world template (first message / outside 24h window)"
                                className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3.5 py-2 text-xs font-semibold text-emerald-700 transition-all duration-200 hover:bg-emerald-100 disabled:opacity-50">
                                <PaperPlaneTilt size={13} weight="fill" /> hello_world
                            </button>
                            <button data-testid="wa-panel-toggle" onClick={() => setPanelOpen((v) => !v)}
                                title={panelOpen ? "Hide contact info" : "Show contact info"} aria-label="Toggle contact info panel"
                                className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border transition-colors duration-200 ${panelOpen ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-stone-300 text-stone2 hover:bg-stone-100"}`}>
                                <SidebarSimple size={18} weight={panelOpen ? "fill" : "regular"} />
                            </button>
                        </div>

                        <div ref={threadRef} className="flex flex-1 flex-col gap-3 overflow-y-auto p-6" data-testid="wa-thread"
                            style={{ backgroundImage: "radial-gradient(rgba(16,24,40,0.035) 1px, transparent 1px)", backgroundSize: "18px 18px" }}>
                            {messages.map((m) => (
                                <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                                    <div className={`max-w-[78%] px-3.5 py-2.5 text-sm shadow-sm ring-1 ${m.direction === "out"
                                        ? "rounded-2xl rounded-tr-md bg-[#e7fbd8] text-ink ring-emerald-900/5"
                                        : "rounded-2xl rounded-tl-md bg-white text-ink ring-black/5"}`}>
                                        <MessageBody m={m} onDelete={deleteMedia} />
                                        <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-stone-400">
                                            {m.timestamp ? formatTime(m.timestamp, orgTimezone) : ""}
                                            {m.direction === "out" && <StatusTicks status={m.status} />}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {messages.length === 0 && <div className="my-auto text-center text-xs text-stone2">No messages yet. Say hello 👋</div>}
                        </div>

                        <div className="flex items-end gap-2 border-t border-stone-200 bg-white p-3">
                            <input ref={fileRef} type="file" className="hidden" onChange={onPickFile}
                                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,video/mp4" data-testid="wa-file-input" />
                            <button data-testid="wa-attach-button" onClick={() => fileRef.current?.click()} disabled={uploading}
                                title="Attach image or document"
                                className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-stone2 transition-all duration-200 hover:bg-stone-100 hover:text-ink disabled:opacity-50">
                                {uploading ? <CircleNotch size={19} className="animate-spin" /> : <Paperclip size={19} />}
                            </button>
                            <input data-testid="wa-compose-input" value={text} onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                                placeholder="Type a message"
                                className="flex-1 rounded-full border border-stone-300 bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                            <button data-testid="wa-send-button" onClick={send} disabled={sending || !text.trim()}
                                className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-emerald-600 text-white transition-all duration-200 hover:bg-emerald-700 disabled:opacity-50">
                                <PaperPlaneTilt size={18} weight="fill" />
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Contact info panel */}
            <div data-testid="wa-info-panel"
                className={`flex shrink-0 flex-col overflow-hidden bg-white transition-all duration-300 ease-out ${active && panelOpen ? "w-[300px] border-l border-stone-200" : "w-0 border-l-0"}`}>
                {active && (() => {
                    const isAdmin = user?.role === "admin";
                    const nm = info?.contact_name || convos.find((c) => c.contact === active)?.contact_name;
                    const sf = info?.salesforce || {};
                    return (
                        <div className="flex h-full w-[300px] flex-col overflow-y-auto">
                            <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Contact info</span>
                                <button data-testid="wa-panel-close" onClick={() => setPanelOpen(false)} aria-label="Close panel"
                                    className="grid h-7 w-7 place-items-center rounded-full text-stone-400 transition-colors hover:bg-stone-100 hover:text-ink">
                                    <X size={15} />
                                </button>
                            </div>

                            <div className="flex flex-col items-center gap-2 border-b border-stone-100 px-5 py-6 text-center">
                                <Avatar label={nm || active} colorKey={active} size="h-16 w-16" text="text-lg" />
                                <div className="font-heading text-base font-bold text-ink">{nm || `+${active}`}</div>
                                <div className="text-xs text-stone2">+{active}</div>
                                {info?.needs_agent && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                                        <User size={10} weight="fill" /> Needs agent
                                    </span>
                                )}
                            </div>

                            {/* Contact details (editable custom attributes) */}
                            <div className="flex flex-col gap-2 border-b border-stone-100 px-5 py-4" data-testid="wa-contact-details">
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400">Contact details</span>
                                    {!editC ? (
                                        <button data-testid="wa-contact-edit" onClick={startEditContact}
                                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 hover:text-emerald-700">
                                            <GearSix size={12} /> Edit
                                        </button>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <button data-testid="wa-contact-cancel" onClick={() => setEditC(false)} className="text-[11px] font-semibold text-stone-400 hover:text-ink">Cancel</button>
                                            <button data-testid="wa-contact-save" onClick={saveContact} disabled={savingC}
                                                className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                                                {savingC ? <CircleNotch size={11} className="animate-spin" /> : <Check size={11} weight="bold" />} Save
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {!editC ? (
                                    <div className="flex flex-col gap-2 text-sm">
                                        <DetailRow icon={Envelope} label="Email" value={info?.email} />
                                        <DetailRow icon={Buildings} label="City" value={info?.city} />
                                        {(info?.tags || []).length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 pt-0.5">
                                                {info.tags.map((t) => (
                                                    <span key={t} className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700">{t}</span>
                                                ))}
                                            </div>
                                        )}
                                        {Object.entries(info?.attributes || {}).map(([k, v]) => (
                                            <DetailRow key={k} label={k} value={v} />
                                        ))}
                                        {!info?.email && !info?.city && !(info?.tags || []).length && !Object.keys(info?.attributes || {}).length && (
                                            <p className="text-xs text-stone-400">No details yet. Click Edit to add.</p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-2.5 pt-1">
                                        <FieldInput testid="wa-cf-name" label="Name" value={cForm.name} onChange={(v) => setCForm((f) => ({ ...f, name: v }))} />
                                        <FieldInput testid="wa-cf-email" label="Email" value={cForm.email} onChange={(v) => setCForm((f) => ({ ...f, email: v }))} />
                                        <FieldInput testid="wa-cf-city" label="City" value={cForm.city} onChange={(v) => setCForm((f) => ({ ...f, city: v }))} />
                                        <FieldInput testid="wa-cf-tags" label="Tags (comma separated)" value={cForm.tags} onChange={(v) => setCForm((f) => ({ ...f, tags: v }))} />
                                        <div className="flex flex-col gap-1.5">
                                            <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Custom attributes</span>
                                            {cForm.attrs.map((a, i) => (
                                                <div key={i} className="flex items-center gap-1.5">
                                                    <input data-testid={`wa-cf-attr-key-${i}`} value={a.k} placeholder="Key" onChange={(e) => setCForm((f) => ({ ...f, attrs: f.attrs.map((x, j) => j === i ? { ...x, k: e.target.value } : x) }))}
                                                        className="w-1/2 rounded-lg border border-stone-300 px-2 py-1.5 text-xs outline-none focus:border-emerald-500" />
                                                    <input data-testid={`wa-cf-attr-val-${i}`} value={a.v} placeholder="Value" onChange={(e) => setCForm((f) => ({ ...f, attrs: f.attrs.map((x, j) => j === i ? { ...x, v: e.target.value } : x) }))}
                                                        className="w-1/2 rounded-lg border border-stone-300 px-2 py-1.5 text-xs outline-none focus:border-emerald-500" />
                                                    <button onClick={() => setCForm((f) => ({ ...f, attrs: f.attrs.filter((_, j) => j !== i) }))} className="text-stone-400 hover:text-red-500"><Trash size={13} /></button>
                                                </div>
                                            ))}
                                            <button data-testid="wa-cf-add-attr" onClick={() => setCForm((f) => ({ ...f, attrs: [...f.attrs, { k: "", v: "" }] }))}
                                                className="inline-flex w-fit items-center gap-1 text-[11px] font-semibold text-emerald-600 hover:text-emerald-700">
                                                <PlusCircle size={12} weight="fill" /> Add attribute
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Assignment */}
                            <div className="flex flex-col gap-2 border-b border-stone-100 px-5 py-4">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400">Assigned agent</span>
                                {canAssign ? (
                                    <select data-testid="wa-assign-select" disabled={assigning}
                                        value={convos.find((c) => c.contact === active)?.assigned_to || info?.assigned_to || ""}
                                        onChange={(e) => assign(e.target.value)}
                                        className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-ink outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20">
                                        <option value="">Unassigned</option>
                                        {agents.map((a) => <option key={a.id} value={a.id}>{a.name || a.email}</option>)}
                                    </select>
                                ) : (
                                    info?.assigned_to_name ? (
                                        <span data-testid="wa-assignee-badge" className="inline-flex w-fit items-center gap-1.5 rounded-full bg-sky-100 px-3 py-1.5 text-xs font-semibold text-sky-700">
                                            <User size={12} weight="fill" /> {info.assigned_to_name}
                                        </span>
                                    ) : (
                                        <span data-testid="wa-assignee-badge" className="inline-flex w-fit items-center gap-1.5 rounded-full bg-stone-100 px-3 py-1.5 text-xs font-semibold text-stone-500">
                                            <User size={12} /> Unassigned
                                        </span>
                                    )
                                )}
                            </div>

                            {/* WhatsApp status */}
                            <div className="flex flex-col gap-3 border-b border-stone-100 px-5 py-4">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400">WhatsApp status</span>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="flex items-center gap-1.5 text-stone2"><Clock size={14} /> 24-hour window</span>
                                    {info?.window?.open
                                        ? <span data-testid="wa-window-status" className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Open</span>
                                        : <span data-testid="wa-window-status" className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-semibold text-stone-500">Closed</span>}
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-stone2">Last active</span>
                                    <span className="text-xs font-medium text-ink">{info?.last_active ? formatDateTime(info.last_active, orgTimezone) : "—"}</span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-stone2">Subscription</span>
                                    {info?.opted_out
                                        ? <span data-testid="wa-optout-status" className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">Opted out</span>
                                        : <span data-testid="wa-optout-status" className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Opted in</span>}
                                </div>
                                {isAdmin && (
                                    <button data-testid="wa-optout-toggle" onClick={toggleOptout} disabled={optBusy}
                                        className={`mt-1 inline-flex items-center justify-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${info?.opted_out ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-red-300 text-red-600 hover:bg-red-50"}`}>
                                        {optBusy ? <CircleNotch size={13} className="animate-spin" /> : info?.opted_out ? <ArrowCounterClockwise size={13} /> : <Prohibit size={13} />}
                                        {info?.opted_out ? "Re-subscribe" : "Opt out contact"}
                                    </button>
                                )}
                            </div>

                            {/* Salesforce */}
                            {sf.connected && (
                                <div className="flex flex-col gap-2 border-b border-stone-100 px-5 py-4">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400">Salesforce</span>
                                    {sf.matched ? (
                                        <>
                                            <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                                                <div className="text-sm font-semibold text-ink">{sf.name || "—"}</div>
                                                {sf.company && <div className="text-xs text-stone2">{sf.company}</div>}
                                                <span className="mt-1 inline-block rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-700">{sf.type}</span>
                                            </div>
                                            <a data-testid="wa-sf-open" href={sf.record_url} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-sky-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-sky-700">
                                                <ArrowSquareOut size={13} weight="bold" /> Open in Salesforce
                                            </a>
                                        </>
                                    ) : (
                                        <p className="text-sm text-stone-400">No matching record found for this number.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}
            </div>
        </div>
    );
}

/* ---------------- Templates ---------------- */
function highlightVars(text) {
    const parts = (text || "").split(/(\{\{\s*\d+\s*\}\})/g);
    return parts.map((p, i) =>
        /\{\{\s*\d+\s*\}\}/.test(p)
            ? <span key={i} className="rounded bg-stone-100 px-1 font-mono text-xs text-emerald-700">{p}</span>
            : <span key={i}>{p}</span>);
}

// Work out which parameters a template needs so we can send it correctly to Meta.
function computeParams(t) {
    const comps = t.components || [];
    const header = comps.find((c) => c.type === "HEADER");
    const body = comps.find((c) => c.type === "BODY");
    const buttonsComp = comps.find((c) => c.type === "BUTTONS");
    const carousel = comps.find((c) => c.type === "CAROUSEL");
    const countVars = (s) => ((s || "").match(/\{\{\s*\d+\s*\}\}/g) || []).length;
    let headerMedia = null, headerTextVars = 0;
    if (header) {
        if (["IMAGE", "VIDEO", "DOCUMENT"].includes(header.format)) headerMedia = header.format.toLowerCase();
        else if (header.format === "TEXT") headerTextVars = countVars(header.text);
    }
    const urlButtons = [];
    (buttonsComp?.buttons || []).forEach((b, i) => {
        if (b.type === "URL" && /\{\{\s*\d+\s*\}\}/.test(b.url || "")) urlButtons.push(i);
    });
    return { headerMedia, headerTextVars, bodyVars: countVars(body?.text), urlButtons, unsupported: !!carousel };
}

function Templates() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState(null);
    const [error, setError] = useState("");
    const [modal, setModal] = useState(null);
    const [sending, setSending] = useState(false);
    const [builder, setBuilder] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        setError("");
        try { const { data } = await api.get("/whatsapp/templates"); setItems(data); }
        catch (e) { setError(formatApiError(e?.response?.data?.detail)); setItems([]); }
    };
    useEffect(() => { load(); }, []);

    const openSend = (t) => {
        const p = computeParams(t);
        setModal({
            template: t, p, to: "", headerLink: "",
            headerText: Array(p.headerTextVars).fill(""),
            body: Array(p.bodyVars).fill(""),
            buttons: Array(p.urlButtons.length).fill(""),
        });
    };

    const doSend = async () => {
        if (!modal.to.trim()) { toast.error("Enter a recipient number"); return; }
        const { template: t, p } = modal;
        if (p.headerMedia && !modal.headerLink.trim()) { toast.error("Add the header media URL"); return; }
        const comps = [];
        if (p.headerMedia) comps.push({ type: "header", parameters: [{ type: p.headerMedia, [p.headerMedia]: { link: modal.headerLink.trim() } }] });
        else if (p.headerTextVars > 0) comps.push({ type: "header", parameters: modal.headerText.map((v) => ({ type: "text", text: v || " " })) });
        if (p.bodyVars > 0) comps.push({ type: "body", parameters: modal.body.map((v) => ({ type: "text", text: v || " " })) });
        p.urlButtons.forEach((idx, k) => comps.push({ type: "button", sub_type: "url", index: idx, parameters: [{ type: "text", text: modal.buttons[k] || " " }] }));
        setSending(true);
        try {
            await api.post("/whatsapp/send", {
                to: modal.to, type: "template", template_name: t.name,
                language: t.language || "en_US", components: comps.length ? comps : null,
            });
            toast.success(`Template "${t.name}" sent`);
            setModal(null);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSending(false); }
    };

    if (items === null) return <div className="p-10 text-center text-sm text-stone2"><CircleNotch size={22} className="mx-auto animate-spin text-emerald-500" /></div>;

    const openBuilder = () => setBuilder({ name: "", category: "UTILITY", language: "en_US", header_text: "", body_text: "", footer_text: "", examples: [], buttons: [] });
    const builderVarCount = builder ? (builder.body_text.match(/\{\{\s*\d+\s*\}\}/g) || []).length : 0;
    const submitBuilder = async () => {
        if (!builder.name.trim() || !builder.body_text.trim()) { toast.error("Name and body are required"); return; }
        setSubmitting(true);
        try {
            await api.post("/whatsapp/templates", {
                name: builder.name, category: builder.category, language: builder.language,
                header_text: builder.header_text || null, body_text: builder.body_text,
                footer_text: builder.footer_text || null,
                body_examples: Array.from({ length: builderVarCount }, (_, i) => builder.examples[i] || "sample"),
                buttons: builder.buttons.filter((b) => b.trim()).map((t) => ({ type: "QUICK_REPLY", text: t })),
            });
            toast.success("Template submitted to Meta for approval");
            setBuilder(null);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSubmitting(false); }
    };

    return (
        <div>
            <div className="mb-5 flex items-center justify-between gap-3">
                <p className="text-sm text-stone2">Your Meta templates. Use approved ones to start conversations, or create a new one for approval.</p>
                <div className="flex shrink-0 gap-2">
                    {isAdmin && (
                        <button data-testid="wa-template-create-open" onClick={openBuilder}
                            className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-2 text-xs font-semibold text-white transition-all duration-200 hover:-translate-y-0.5">
                            <PlusCircle size={14} weight="fill" /> Create template
                        </button>
                    )}
                    <button data-testid="wa-templates-refresh" onClick={load}
                        className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3.5 py-2 text-xs font-semibold text-stone2 transition-all duration-200 hover:border-ink hover:text-ink">
                        <ArrowClockwise size={14} /> Refresh
                    </button>
                </div>
            </div>

            {error && <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700" data-testid="wa-templates-error">{error}</div>}
            {!error && items.length === 0 && <div className="rounded-xl border border-stone-200 bg-white p-10 text-center text-sm text-stone2">No templates found on your WhatsApp Business Account yet.</div>}

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="wa-templates-grid">
                {items.map((t) => {
                    const cp = computeParams(t);
                    const disabled = t.status !== "APPROVED" || cp.unsupported;
                    return (
                        <div key={t.name} data-testid={`wa-template-card-${t.name}`}
                            className="flex flex-col gap-3 rounded-xl border border-stone-200 bg-white p-5 shadow-sm transition-all duration-200 hover:shadow-md">
                            <div className="flex items-start justify-between gap-2">
                                <div className="font-heading font-semibold text-ink">{t.name}</div>
                                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${t.status === "APPROVED" ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone2"}`}>{t.status}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5 text-[11px] uppercase tracking-wider text-stone2">
                                <span>{t.language}</span>{t.category && <><span>·</span><span>{t.category}</span></>}
                                {cp.headerMedia && <span className="rounded bg-sky-50 px-1.5 text-sky-600">{cp.headerMedia} header</span>}
                                {cp.bodyVars > 0 && <span className="rounded bg-stone-100 px-1.5">{cp.bodyVars} var{cp.bodyVars > 1 ? "s" : ""}</span>}
                                {cp.unsupported && <span className="rounded bg-amber-50 px-1.5 text-amber-600">carousel</span>}
                            </div>
                            <div className="min-h-[48px] flex-1 rounded-lg bg-stone-50 p-3 text-sm text-ink">{t.body_text ? highlightVars(t.body_text) : <span className="text-stone-400">Media / non-text template</span>}</div>
                            <button data-testid={`wa-template-send-${t.name}`} onClick={() => openSend(t)} disabled={disabled}
                                title={cp.unsupported ? "Carousel templates aren't supported in quick-send yet" : ""}
                                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-ink px-4 py-2 text-xs font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-40">
                                <PaperPlaneTilt size={13} weight="fill" /> {cp.unsupported ? "Not supported" : "Send template"}
                            </button>
                        </div>
                    );
                })}
            </div>

            {modal && (
                <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setModal(null)}>
                    <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="wa-template-modal">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="font-heading text-lg font-bold">Send "{modal.template.name}"</h3>
                            <button onClick={() => setModal(null)} className="text-stone-400 hover:text-ink"><X size={18} /></button>
                        </div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Recipient number</label>
                        <input data-testid="wa-template-to" value={modal.to} onChange={(e) => setModal({ ...modal, to: e.target.value })}
                            placeholder="e.g. 919812345678"
                            className="mb-4 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />

                        {modal.p.headerMedia && (
                            <div className="mb-3">
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Header {modal.p.headerMedia} URL</label>
                                <input data-testid="wa-template-header-link" value={modal.headerLink} onChange={(e) => setModal({ ...modal, headerLink: e.target.value })}
                                    placeholder="https://…/file" className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                                <p className="mt-1 text-[11px] text-stone2">Public URL to the {modal.p.headerMedia} shown at the top of this template.</p>
                            </div>
                        )}
                        {modal.headerText.map((v, i) => (
                            <div key={`h${i}`} className="mb-3">
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Header variable {`{{${i + 1}}}`}</label>
                                <input data-testid={`wa-template-header-var-${i}`} value={v}
                                    onChange={(e) => { const nv = [...modal.headerText]; nv[i] = e.target.value; setModal({ ...modal, headerText: nv }); }}
                                    className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                            </div>
                        ))}
                        {modal.body.map((v, i) => (
                            <div key={`b${i}`} className="mb-3">
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Body variable {`{{${i + 1}}}`}</label>
                                <input data-testid={`wa-template-var-${i}`} value={v}
                                    onChange={(e) => { const nv = [...modal.body]; nv[i] = e.target.value; setModal({ ...modal, body: nv }); }}
                                    className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                            </div>
                        ))}
                        {modal.buttons.map((v, i) => (
                            <div key={`btn${i}`} className="mb-3">
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Button URL variable</label>
                                <input data-testid={`wa-template-btn-var-${i}`} value={v}
                                    onChange={(e) => { const nv = [...modal.buttons]; nv[i] = e.target.value; setModal({ ...modal, buttons: nv }); }}
                                    className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                            </div>
                        ))}

                        <button data-testid="wa-template-confirm-send" onClick={doSend} disabled={sending}
                            className="mt-2 w-full rounded-full bg-emerald-600 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-emerald-700 disabled:opacity-50">
                            {sending ? "Sending…" : "Send template"}
                        </button>
                        <p className="mt-2 text-center text-[11px] text-stone2">Tip: with a Meta test number, the recipient must be in your allowed test list.</p>
                    </div>
                </div>
            )}

            {builder && (
                <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setBuilder(null)}>
                    <div className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="wa-template-builder">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="font-heading text-lg font-bold">Create WhatsApp template</h3>
                            <button onClick={() => setBuilder(null)} className="text-stone-400 hover:text-ink"><X size={18} /></button>
                        </div>
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Name</label>
                                    <input data-testid="wa-builder-name" value={builder.name}
                                        onChange={(e) => setBuilder({ ...builder, name: e.target.value.toLowerCase().replace(/[^a-z0-9_ ]/g, "").replace(/ /g, "_") })}
                                        placeholder="order_update" className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                                </div>
                                <div>
                                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Category</label>
                                    <select data-testid="wa-builder-category" value={builder.category} onChange={(e) => setBuilder({ ...builder, category: e.target.value })}
                                        className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-emerald-500">
                                        <option value="UTILITY">Utility</option>
                                        <option value="MARKETING">Marketing</option>
                                        <option value="AUTHENTICATION">Authentication</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Header text (optional)</label>
                                <input data-testid="wa-builder-header" value={builder.header_text} onChange={(e) => setBuilder({ ...builder, header_text: e.target.value })}
                                    className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Body <span className="normal-case text-stone-400">— use {`{{1}}`}, {`{{2}}`} for variables</span></label>
                                <textarea data-testid="wa-builder-body" rows={3} value={builder.body_text} onChange={(e) => setBuilder({ ...builder, body_text: e.target.value })}
                                    placeholder="Hi {{1}}, your order {{2}} is confirmed."
                                    className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                            </div>
                            {Array.from({ length: builderVarCount }).map((_, i) => (
                                <div key={i}>
                                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Example for {`{{${i + 1}}}`}</label>
                                    <input data-testid={`wa-builder-example-${i}`} value={builder.examples[i] || ""}
                                        onChange={(e) => { const ex = [...builder.examples]; ex[i] = e.target.value; setBuilder({ ...builder, examples: ex }); }}
                                        placeholder="Priya" className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                                </div>
                            ))}
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Footer (optional)</label>
                                <input data-testid="wa-builder-footer" value={builder.footer_text} onChange={(e) => setBuilder({ ...builder, footer_text: e.target.value })}
                                    className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                            </div>
                            <div>
                                <div className="mb-1 flex items-center justify-between">
                                    <label className="text-xs font-semibold uppercase tracking-wide text-stone2">Quick-reply buttons (optional)</label>
                                    {builder.buttons.length < 3 && (
                                        <button data-testid="wa-builder-add-button" onClick={() => setBuilder({ ...builder, buttons: [...builder.buttons, ""] })}
                                            className="text-xs font-semibold text-emerald-700 hover:underline">+ Add button</button>
                                    )}
                                </div>
                                {builder.buttons.map((b, i) => (
                                    <input key={i} data-testid={`wa-builder-button-${i}`} value={b}
                                        onChange={(e) => { const bs = [...builder.buttons]; bs[i] = e.target.value; setBuilder({ ...builder, buttons: bs }); }}
                                        placeholder={`Button ${i + 1} text`} className="mb-2 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                                ))}
                            </div>
                        </div>
                        <button data-testid="wa-builder-submit" onClick={submitBuilder} disabled={submitting}
                            className="mt-4 w-full rounded-full bg-emerald-600 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-emerald-700 disabled:opacity-50">
                            {submitting ? "Submitting…" : "Submit to Meta for approval"}
                        </button>
                        <p className="mt-2 text-center text-[11px] text-stone2">Meta reviews new templates (usually minutes–hours). It appears here as PENDING, then APPROVED.</p>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------------- Setup ---------------- */
function SetupChecklist({ status, checking, onRecheck }) {
    const s = status?.steps || {};
    const items = [
        { key: "credentials", label: "Credentials saved", ok: s.credentials?.ok,
          detail: s.credentials?.ok ? "Phone Number ID + Access Token present" : "Add your Phone Number ID and Access Token below" },
        { key: "connection", label: "WhatsApp number verified", ok: s.connection?.ok,
          detail: s.connection?.ok ? `${s.connection?.verified_name || ""} ${s.connection?.display_phone_number || ""}`.trim() : (s.connection?.error || "Not verified yet") },
        { key: "webhook_subscribed", label: "Webhook subscribed to messages", ok: s.webhook_subscribed?.ok,
          detail: s.webhook_subscribed?.ok ? "Your app receives inbound messages" : (s.webhook_subscribed?.error || "Not subscribed") },
        { key: "bot", label: "Auto-reply bot configured", ok: s.bot?.ok,
          detail: s.bot?.ok ? `${s.bot?.options} option(s)${s.bot?.enabled ? " · enabled" : " · disabled"}` : "Build a flow in the Bot tab (optional)" },
    ];
    const doneCount = items.filter((i) => i.ok).length;
    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="wa-setup-checklist">
            <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                    <h3 className="font-heading text-lg font-bold">Onboarding checklist</h3>
                    <p className="mt-0.5 text-sm text-stone2">{doneCount}/{items.length} steps complete{status?.ready ? " · you're live-ready 🎉" : ""}</p>
                </div>
                <button data-testid="wa-test-webhook" onClick={onRecheck} disabled={checking}
                    className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50">
                    {checking ? <CircleNotch size={15} className="animate-spin" /> : <ArrowClockwise size={15} />}
                    {checking ? "Checking…" : "Test webhook"}
                </button>
            </div>
            <ul className="space-y-2">
                {items.map((it) => (
                    <li key={it.key} data-testid={`wa-check-${it.key}`} className="flex items-start gap-3 rounded-xl border border-stone-100 bg-stone-50/50 px-4 py-3">
                        <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${it.ok ? "bg-emerald-100 text-emerald-600" : "bg-stone-200 text-stone-400"}`}>
                            {it.ok ? <Check size={13} weight="bold" /> : <span className="text-[11px] font-bold">•</span>}
                        </span>
                        <div className="min-w-0">
                            <div className={`text-sm font-semibold ${it.ok ? "text-ink" : "text-stone-500"}`}>{it.label}</div>
                            {it.detail && <div className="truncate text-xs text-stone2">{it.detail}</div>}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function Setup() {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({ phone_number_id: "", waba_id: "", display_phone_number: "", access_token: "", app_secret: "", app_id: "" });
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [registering, setRegistering] = useState(false);
    const [status, setStatus] = useState(null);
    const [checking, setChecking] = useState(false);

    const checkSetup = async () => {
        setChecking(true);
        try { const { data } = await api.get("/whatsapp/setup-status"); setStatus(data); }
        catch (_e) { /* ignore */ } finally { setChecking(false); }
    };

    const load = async () => {
        const { data } = await api.get("/whatsapp/settings");
        setCfg(data);
        setForm((f) => ({
            ...f, phone_number_id: data.phone_number_id || "", waba_id: data.waba_id || "",
            display_phone_number: data.display_phone_number || "",
            app_id: data.app_id || "",
            access_token: data.has_access_token ? "••••••••••••" : "",
            app_secret: data.has_app_secret ? "••••••••••••" : "",
        }));
    };
    useEffect(() => { load(); checkSetup(); }, []);

    const save = async () => {
        const pid = (form.phone_number_id || "").trim();
        if (pid && !/^\d+$/.test(pid)) {
            toast.error("Phone Number ID must be the numeric ID from Meta → WhatsApp → API Setup (e.g. 1252569711271843) — not the +1 phone number.");
            return;
        }
        setSaving(true);
        try { await api.put("/whatsapp/settings", form); toast.success("WhatsApp settings saved"); await load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setSaving(false); }
    };
    const test = async () => {
        setTesting(true);
        try {
            const { data } = await api.post("/whatsapp/test-connection");
            toast.success(`Connected: ${data.verified_name || data.display_phone_number || "OK"}${data.webhook_subscribed ? " · webhook subscribed" : ""}`);
            await load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setTesting(false); await checkSetup(); }
    };
    const registerWebhook = async () => {
        setRegistering(true);
        try {
            const { data } = await api.post("/whatsapp/register-webhook");
            if (data.ok) toast.success("Webhook registered with Meta — delivery & read receipts will now update.");
            else if (data.registered && !data.subscribed) toast.warning("Webhook URL registered, but couldn't subscribe the number. Click Test connection.");
            else toast.error(data.error || "Couldn't register the webhook. Check your App ID & App Secret.");
            await load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't register the webhook"); }
        finally { setRegistering(false); await checkSetup(); }
    };

    if (!cfg) return <div className="p-8 text-sm text-stone2">Loading…</div>;
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <div className="space-y-6">
            <SetupChecklist status={status} checking={checking} onRecheck={checkSetup} />
            <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
                <div className="mb-1 flex items-center gap-2">
                    <Plugs size={18} className="text-emerald-600" />
                    <h3 className="font-heading text-lg font-bold">Connect Meta WhatsApp</h3>
                    {cfg.connected && <span data-testid="wa-connected-badge" className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700"><CheckCircle size={12} weight="fill" /> Connected</span>}
                </div>
                <p className="mb-4 text-sm text-stone2">Credentials from your Meta WhatsApp Business account (API Setup + App Settings).</p>
                <div className="space-y-3">
                    {[["phone_number_id", "Phone Number ID", "e.g. 1252569711271843", "The numeric ID from Meta → WhatsApp → API Setup — NOT the +1 phone number."],
                      ["waba_id", "WhatsApp Business Account ID", "e.g. 123456789012345", ""],
                      ["display_phone_number", "Display phone number (optional)", "+91 98…", ""]].map(([k, label, ph, hint]) => (
                        <div key={k}>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">{label}</label>
                            <input data-testid={`wa-${k}`} value={form[k]} onChange={set(k)} placeholder={ph}
                                className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                            {hint && <p data-testid={`wa-${k}-hint`} className="mt-1 text-[11px] leading-snug text-stone-400">{hint}</p>}
                        </div>
                    ))}
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Permanent access token</label>
                        <input data-testid="wa-access_token" type="password" value={form.access_token} onChange={set("access_token")} placeholder="EAAG…"
                            className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">App secret (for webhook signature)</label>
                        <input data-testid="wa-app_secret" type="password" value={form.app_secret} onChange={set("app_secret")} placeholder="App Settings → Basic"
                            className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">App ID (for auto webhook setup)</label>
                        <input data-testid="wa-app_id" value={form.app_id} onChange={set("app_id")} placeholder="Meta App dashboard → App ID (e.g. 123456789012345)"
                            className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                        <p className="mt-1 text-[11px] leading-snug text-stone-400">With App ID + App Secret we can auto-register the webhook so delivery/read receipts flow to this environment.</p>
                    </div>
                </div>
                <div className="mt-5 flex gap-2">
                    <button data-testid="wa-save-settings" onClick={save} disabled={saving}
                        className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50">
                        {saving ? "Saving…" : "Save credentials"}
                    </button>
                    <button data-testid="wa-test-connection" onClick={test} disabled={testing || !cfg.connected}
                        className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-sm font-semibold transition-all duration-200 hover:border-ink disabled:opacity-50">
                        <ArrowClockwise size={15} /> {testing ? "Testing…" : "Test connection"}
                    </button>
                </div>
            </div>

            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-6 shadow-sm">
                <h3 className="mb-1 font-heading text-lg font-bold">Webhook configuration</h3>
                <p className="mb-4 text-sm text-stone2">Easiest: add your <b>App ID</b> + <b>App Secret</b> above, then click <b>Auto-register webhook</b> — we point Meta at this workspace's URL and subscribe the <b>messages</b> field (which carries delivery & read receipts). Or set it manually in Meta → WhatsApp → Configuration.</p>
                <div className="mb-4 flex flex-wrap items-center gap-3">
                    <button data-testid="wa-register-webhook" onClick={registerWebhook} disabled={registering || !cfg.connected}
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50">
                        <ArrowClockwise size={15} /> {registering ? "Registering…" : "Auto-register webhook"}
                    </button>
                    <span data-testid="wa-webhook-health" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${status?.last_webhook_at ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                        {status?.last_webhook_at
                            ? `Last event received: ${new Date(status.last_webhook_at).toLocaleString()}`
                            : "No webhook events received yet"}
                    </span>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Callback URL</label>
                        <CopyField value={cfg.webhook_url} testid="wa-webhook-url" />
                        <p className="mt-1 text-xs text-stone2"><b>This URL is unique to this workspace</b> (it carries your workspace ID), so inbound messages always route to the right team even if another workspace also uses WhatsApp.</p>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2">Verify token</label>
                        <CopyField value={cfg.verify_token} testid="wa-verify-token" />
                        {!cfg.verify_token && <p className="mt-1 text-xs text-amber-600">Save your credentials once to generate the verify token.</p>}
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
}

/* ---------------- Salesforce API ---------------- */
const LWC_JS = `// force-app/main/default/lwc/inspiroWhatsapp/inspiroWhatsapp.js
import { LightningElement, api, track, wire } from 'lwc';
import { ShowToastEvent } from 'lightning/platformShowToastEvent';
import { getRecord, getFieldValue } from 'lightning/uiRecordApi';
import getMessages from '@salesforce/apex/InspiroWhatsAppController.getMessages';
import getSession from '@salesforce/apex/InspiroWhatsAppController.getSession';
import getMedia from '@salesforce/apex/InspiroWhatsAppController.getMedia';
import sendMessage from '@salesforce/apex/InspiroWhatsAppController.sendMessage';
import sendMedia from '@salesforce/apex/InspiroWhatsAppController.sendMedia';
import markRead from '@salesforce/apex/InspiroWhatsAppController.markRead';

const PHONE_FIELDS = ['Contact.MobilePhone', 'Contact.Phone', 'Lead.MobilePhone', 'Lead.Phone'];

export default class InspiroWhatsapp extends LightningElement {
    @api recordId;
    @api objectApiName;
    @track messages = [];
    number = '';
    draft = '';
    loading = false;
    sending = false;
    sessionOpen = true;
    sessionExpiresAt = null;
    _lastTs = null;
    _mediaCache = {};
    _poll;

    // Phone fields to try for THIS object (Lead.* on a Lead page, Contact.* on a Contact page).
    get fieldRefs() {
        return PHONE_FIELDS.filter((f) => f.startsWith(this.objectApiName + '.'));
    }

    // Auto-fill the WhatsApp number from the record's phone/mobile field.
    @wire(getRecord, { recordId: '$recordId', fields: '$fieldRefs' })
    wiredRecord({ data }) {
        if (data && !this.number) {
            for (const f of this.fieldRefs) {
                const v = getFieldValue(data, f);
                if (v) { this.number = v; this.load(); break; }
            }
        }
    }

    connectedCallback() {
        this._poll = setInterval(() => { if (this.number) this.refresh(); }, 6000);
    }
    disconnectedCallback() { clearInterval(this._poll); }

    get isEmpty() { return !this.loading && this.messages.length === 0; }
    get composerDisabled() { return this.sending || !this.sessionOpen; }

    handleNumberChange(e) {
        this.number = e.target.value;
        this.messages = []; this._lastTs = null;
        if (this.number) this.load();
    }
    handleDraftChange(e) { this.draft = e.target.value; }

    async load() {
        if (!this.number) return;
        this.loading = true;
        try {
            const data = await getMessages({ contact: this.number, since: null });
            this.messages = []; this._lastTs = null;
            await this.ingest(data);
            await this.loadSession();
            markRead({ contact: this.number }).catch(() => {});
        } catch (err) { this.toast('Error', this.err(err), 'error'); }
        finally { this.loading = false; }
    }

    async refresh() {
        try {
            const data = await getMessages({ contact: this.number, since: this._lastTs });
            if (data && data.length) {
                await this.ingest(data);
                await this.loadSession();
                markRead({ contact: this.number }).catch(() => {});
            }
        } catch (err) { /* silent on poll */ }
    }

    async ingest(data) {
        const rows = [];
        for (const m of data) {
            const isOut = m.direction === 'out';
            const row = { ...m,
                cssClass: isOut ? 'row out' : 'row in',
                isOut,
                displayTime: this.fmtTime(m.timestamp),
                tick: isOut ? this.tickFor(m.status) : '',
                tickClass: 'tick ' + (m.status === 'read' ? 'read' : (m.status === 'failed' ? 'failed' : '')),
                hasMedia: !!m.mediaRef,
                isImage: !!m.mediaMime && m.mediaMime.startsWith('image/'),
                mediaSrc: null };
            if (row.hasMedia && row.isImage) row.mediaSrc = await this.mediaUrl(m.mediaRef);
            rows.push(row);
            if (m.timestamp) this._lastTs = m.timestamp;
        }
        this.messages = [...this.messages, ...rows];
    }

    fmtTime(ts) {
        if (!ts) return '';
        const d = new Date(ts);
        if (isNaN(d.getTime())) return ts;
        const sameDay = d.toDateString() === new Date().toDateString();
        const opts = sameDay
            ? { hour: 'numeric', minute: '2-digit' }
            : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
        return d.toLocaleString(undefined, opts);
    }

    tickFor(status) {
        switch (status) {
            case 'sending': return '🕓';
            case 'sent': return '✓';
            case 'delivered': return '✓✓';
            case 'read': return '✓✓';
            case 'failed': return '⚠';
            default: return '';
        }
    }

    async mediaUrl(ref) {
        if (this._mediaCache[ref]) return this._mediaCache[ref];
        try {
            const media = await getMedia({ mediaRef: ref });
            const url = 'data:' + media.contentType + ';base64,' + media.dataBase64;
            this._mediaCache[ref] = url;
            return url;
        } catch (e) { return null; }
    }

    async loadSession() {
        try {
            const s = await getSession({ contact: this.number });
            this.sessionOpen = !!s.open;
            this.sessionExpiresAt = s.expiresAt;
        } catch (e) { /* keep prior */ }
    }

    async handleSend() {
        if (!this.number || !this.draft) return;
        this.sending = true;
        try {
            await sendMessage({ toNumber: this.number, body: this.draft });
            this.draft = '';
            await this.refresh();
        } catch (err) { this.toast('Send failed', this.err(err), 'error'); }
        finally { this.sending = false; }
    }

    handleAttach() { this.template.querySelector('input.file-input').click(); }

    async handleFile(e) {
        const file = e.target.files && e.target.files[0];
        if (!file || !this.number) return;
        this.sending = true;
        try {
            const b64 = await this.toBase64(file);
            await sendMedia({ toNumber: this.number, filename: file.name,
                contentType: file.type || 'application/octet-stream',
                dataBase64: b64, caption: this.draft || '' });
            this.draft = '';
            await this.refresh();
        } catch (err) { this.toast('Send failed', this.err(err), 'error'); }
        finally { this.sending = false; e.target.value = ''; }
    }

    toBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    err(e) { return (e && e.body && e.body.message) ? e.body.message : 'Unexpected error'; }
    toast(title, message, variant) { this.dispatchEvent(new ShowToastEvent({ title, message, variant })); }
}`;

const LWC_HTML = `<!-- force-app/main/default/lwc/inspiroWhatsapp/inspiroWhatsapp.html -->
<template>
    <lightning-card title="WhatsApp" icon-name="standard:messaging_session">
        <div class="slds-p-horizontal_medium slds-p-bottom_medium">
            <lightning-input type="tel" label="WhatsApp number" value={number}
                onchange={handleNumberChange} placeholder="e.g. 919812345678"></lightning-input>

            <div class="thread slds-box slds-box_x-small slds-m-vertical_small">
                <template if:true={loading}>
                    <lightning-spinner alternative-text="Loading" size="small"></lightning-spinner>
                </template>
                <template if:false={loading}>
                    <template for:each={messages} for:item="m">
                        <div key={m.id} class={m.cssClass}>
                            <div class="bubble">
                                <template if:true={m.isImage}>
                                    <img src={m.mediaSrc} class="media-img" alt={m.filename} />
                                </template>
                                <template if:true={m.hasMedia}>
                                    <template if:false={m.isImage}>
                                        <span class="doc">📎 {m.filename}</span>
                                    </template>
                                </template>
                                <template if:true={m.body}>{m.body}</template>
                                <span class="meta">{m.displayTime}<template if:true={m.isOut}> <span class={m.tickClass}>{m.tick}</span></template></span>
                            </div>
                        </div>
                    </template>
                    <template if:true={isEmpty}>
                        <p class="slds-text-align_center slds-text-color_weak slds-p-around_small">No messages yet.</p>
                    </template>
                </template>
            </div>

            <template if:false={sessionOpen}>
                <div class="session-banner slds-m-bottom_x-small">
                    🔒 Session expired. The customer must message first — or send an approved template.
                </div>
            </template>

            <div class="slds-grid slds-gutters_x-small slds-grid_vertical-align-center">
                <div class="slds-col slds-no-flex">
                    <lightning-button-icon icon-name="utility:attach" alternative-text="Attach file"
                        title="Attach file" disabled={composerDisabled} onclick={handleAttach}></lightning-button-icon>
                    <input type="file" class="file-input" style="display:none" onchange={handleFile} />
                </div>
                <div class="slds-col">
                    <lightning-input type="text" variant="label-hidden" value={draft}
                        disabled={composerDisabled} onchange={handleDraftChange}
                        placeholder="Type a message…"></lightning-input>
                </div>
                <div class="slds-col slds-no-flex">
                    <lightning-button variant="brand" label="Send" onclick={handleSend}
                        disabled={composerDisabled}></lightning-button>
                </div>
            </div>
        </div>
    </lightning-card>
</template>`;

const LWC_META = `<!-- inspiroWhatsapp.js-meta.xml — REQUIRED so the component is placeable on record pages -->
<?xml version="1.0" encoding="UTF-8"?>
<LightningComponentBundle xmlns="http://soap.sforce.com/2006/04/metadata">
    <apiVersion>60.0</apiVersion>
    <isExposed>true</isExposed>
    <targets>
        <target>lightning__RecordPage</target>
        <target>lightning__AppPage</target>
        <target>lightning__HomePage</target>
    </targets>
</LightningComponentBundle>`;

const LWC_CSS = `/* force-app/main/default/lwc/inspiroWhatsapp/inspiroWhatsapp.css */
.thread { max-height: 260px; overflow-y: auto; background: #efeae2; }
.row { display: flex; margin: 4px 0; }
.row.out { justify-content: flex-end; }
.row.in { justify-content: flex-start; }
.bubble {
    max-width: 78%; padding: 6px 10px; border-radius: 10px;
    font-size: 0.8rem; line-height: 1.3; background: #ffffff;
    box-shadow: 0 1px 1px rgba(0,0,0,0.08);
    white-space: pre-wrap; word-break: break-word;
}
.row.out .bubble { background: #d9fdd3; }
.meta { display: block; margin-top: 2px; font-size: 0.62rem; color: #667781; text-align: right; }
.tick { margin-left: 2px; font-weight: 700; letter-spacing: -1px; }
.tick.read { color: #53bdeb; }
.tick.failed { color: #e02f2f; letter-spacing: 0; }
.media-img { display: block; max-width: 100%; border-radius: 6px; margin-bottom: 4px; }
.doc { display: inline-block; font-size: 0.78rem; color: #075e54; margin-bottom: 4px; }
.session-banner {
    background: #fff4d6; border: 1px solid #f2d98a; border-radius: 6px;
    padding: 6px 10px; font-size: 0.72rem; color: #7a5b00; text-align: center;
}`;

const APEX_CODE = `// force-app/main/default/classes/InspiroWhatsAppController.cls
public with sharing class InspiroWhatsAppController {

    // Named Credential that points to your Inspiro base URL and injects the X-API-Key header.
    private static final String NC = 'callout:Inspiro_WhatsApp';

    public class WaMessage {
        @AuraEnabled public String id;
        @AuraEnabled public String contact;
        @AuraEnabled public String direction;   // in | out
        @AuraEnabled public String type;
        @AuraEnabled public String body;
        @AuraEnabled public String status;       // sending | sent | delivered | read | failed
        @AuraEnabled public String timestamp;
        @AuraEnabled public String mediaRef;
        @AuraEnabled public String mediaMime;
        @AuraEnabled public String filename;
        @AuraEnabled public Boolean read;
    }
    public class WaSession {
        @AuraEnabled public Boolean open;
        @AuraEnabled public String expiresAt;
        @AuraEnabled public String lastInboundAt;
    }
    public class WaMedia {
        @AuraEnabled public String contentType;
        @AuraEnabled public String dataBase64;
    }

    private static HttpResponse doCall(String method, String path, String bodyJson) {
        HttpRequest req = new HttpRequest();
        req.setEndpoint(NC + path);
        req.setMethod(method);
        req.setTimeout(60000);
        // X-API-Key is injected by the Named/External Credential (Custom Header) — not set here.
        req.setHeader('X-Agent-Email', UserInfo.getUserEmail());  // per-agent attribution
        if (bodyJson != null) {
            req.setHeader('Content-Type', 'application/json');
            req.setBody(bodyJson);
        }
        HttpResponse res = new Http().send(req);
        String peek = res.getBody();
        if (peek != null && peek.trim().startsWith('<')) {
            throw new AuraHandledException('Inspiro WhatsApp: received an HTML page instead of JSON. Your Named Credential URL is pointing at the web app, not the API. Set it to the Inspiro API base URL — include the /backend path if your API is served under it (e.g. https://your-domain.com/backend).');
        }
        if (res.getStatusCode() >= 300) {
            String msg = res.getBody();
            try {
                Map<String, Object> m = (Map<String, Object>) JSON.deserializeUntyped(res.getBody());
                if (m.containsKey('detail')) msg = String.valueOf(m.get('detail'));
            } catch (Exception e) { /* keep raw */ }
            throw new AuraHandledException('Inspiro WhatsApp error: ' + msg);
        }
        return res;
    }

    @AuraEnabled(cacheable=false)
    public static List<WaMessage> getMessages(String contact, String since) {
        String path = '/api/whatsapp/external/messages?contact=' + EncodingUtil.urlEncode(contact, 'UTF-8');
        if (String.isNotBlank(since)) path += '&since=' + EncodingUtil.urlEncode(since, 'UTF-8');
        HttpResponse res = doCall('GET', path, null);
        List<Object> raw = (List<Object>) JSON.deserializeUntyped(res.getBody());
        List<WaMessage> out = new List<WaMessage>();
        for (Object o : raw) {
            Map<String, Object> m = (Map<String, Object>) o;
            WaMessage w = new WaMessage();
            w.id = String.valueOf(m.get('id'));
            w.contact = String.valueOf(m.get('contact'));
            w.direction = String.valueOf(m.get('direction'));
            w.type = String.valueOf(m.get('type'));
            w.body = m.get('body') == null ? '' : String.valueOf(m.get('body'));
            w.status = String.valueOf(m.get('status'));
            w.timestamp = String.valueOf(m.get('timestamp'));
            w.mediaRef = m.get('media_ref') == null ? null : String.valueOf(m.get('media_ref'));
            w.mediaMime = m.get('media_mime') == null ? null : String.valueOf(m.get('media_mime'));
            w.filename = m.get('filename') == null ? null : String.valueOf(m.get('filename'));
            w.read = m.get('read') == true;
            out.add(w);
        }
        return out;
    }

    @AuraEnabled(cacheable=false)
    public static WaSession getSession(String contact) {
        HttpResponse res = doCall('GET',
            '/api/whatsapp/external/session?contact=' + EncodingUtil.urlEncode(contact, 'UTF-8'), null);
        Map<String, Object> m = (Map<String, Object>) JSON.deserializeUntyped(res.getBody());
        WaSession s = new WaSession();
        s.open = m.get('open') == true;
        s.expiresAt = m.get('expires_at') == null ? null : String.valueOf(m.get('expires_at'));
        s.lastInboundAt = m.get('last_inbound_at') == null ? null : String.valueOf(m.get('last_inbound_at'));
        return s;
    }

    @AuraEnabled(cacheable=false)
    public static List<Object> getTemplates() {
        HttpResponse res = doCall('GET', '/api/whatsapp/external/templates', null);
        return (List<Object>) JSON.deserializeUntyped(res.getBody());
    }

    @AuraEnabled(cacheable=false)
    public static WaMedia getMedia(String mediaRef) {
        HttpResponse res = doCall('GET',
            '/api/whatsapp/external/media-base64/' + EncodingUtil.urlEncode(mediaRef, 'UTF-8'), null);
        Map<String, Object> m = (Map<String, Object>) JSON.deserializeUntyped(res.getBody());
        WaMedia w = new WaMedia();
        w.contentType = String.valueOf(m.get('content_type'));
        w.dataBase64 = String.valueOf(m.get('data_base64'));
        return w;
    }

    @AuraEnabled
    public static void sendMessage(String toNumber, String body) {
        doCall('POST', '/api/whatsapp/external/send', JSON.serialize(new Map<String, Object>{
            'to' => toNumber, 'type' => 'text', 'body' => body }));
    }

    @AuraEnabled
    public static void sendTemplate(String toNumber, String templateName, String language, List<String> variables) {
        Map<String, Object> payload = new Map<String, Object>{
            'to' => toNumber, 'type' => 'template',
            'template_name' => templateName, 'language' => (String.isBlank(language) ? 'en_US' : language) };
        if (variables != null && !variables.isEmpty()) {
            List<Object> params = new List<Object>();
            for (String v : variables) params.add(new Map<String, Object>{ 'type' => 'text', 'text' => v });
            payload.put('components', new List<Object>{
                new Map<String, Object>{ 'type' => 'body', 'parameters' => params } });
        }
        doCall('POST', '/api/whatsapp/external/send', JSON.serialize(payload));
    }

    @AuraEnabled
    public static void sendMedia(String toNumber, String filename, String contentType, String dataBase64, String caption) {
        doCall('POST', '/api/whatsapp/external/send-media', JSON.serialize(new Map<String, Object>{
            'to' => toNumber, 'filename' => filename, 'content_type' => contentType,
            'data_base64' => dataBase64, 'caption' => (caption == null ? '' : caption) }));
    }

    @AuraEnabled
    public static void markRead(String contact) {
        doCall('POST', '/api/whatsapp/external/mark-read',
            JSON.serialize(new Map<String, Object>{ 'contact' => contact }));
    }
}`;

const APEX_META = `<!-- classes/InspiroWhatsAppController.cls-meta.xml (required for CLI deploy) -->
<?xml version="1.0" encoding="UTF-8"?>
<ApexClass xmlns="http://soap.sforce.com/2006/04/metadata">
    <apiVersion>60.0</apiVersion>
    <status>Active</status>
</ApexClass>`;

const CODE_FILES = { js: LWC_JS, html: LWC_HTML, css: LWC_CSS, meta: LWC_META, apex: APEX_CODE, apexmeta: APEX_META };

function LeadPagePreview() {
    return (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-stone-100 shadow-sm" data-testid="wa-sf-preview">
            {/* Salesforce-style highlights bar */}
            <div className="flex items-center gap-3 border-b border-stone-200 bg-white px-5 py-3">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-orange-500 text-white"><Buildings size={18} weight="fill" /></div>
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-stone2">Lead</div>
                    <div className="font-heading text-base font-bold text-ink">Priya Sharma</div>
                </div>
                <div className="ml-auto hidden gap-6 sm:flex">
                    {[["Company", "Nimbus Retail"], ["Phone", "+91 98104 12345"], ["Status", "Working"], ["Rating", "Hot"]].map(([k, v]) => (
                        <div key={k} className="border-l border-stone-200 pl-6">
                            <div className="text-[10px] uppercase tracking-wide text-stone2">{k}</div>
                            <div className="text-sm font-semibold text-ink">{v}</div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="grid gap-4 p-4 lg:grid-cols-5">
                {/* Left: details */}
                <div className="lg:col-span-3">
                    <div className="rounded-lg border border-stone-200 bg-white p-4">
                        <div className="mb-3 text-xs font-bold uppercase tracking-wider text-stone2">Lead Details</div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            {[["Name", "Priya Sharma"], ["Title", "Head of Procurement"], ["Email", "priya@nimbus.co"], ["Lead Source", "Website"], ["Industry", "Retail"], ["Owner", "You"]].map(([k, v]) => (
                                <div key={k}>
                                    <div className="text-[11px] text-stone2">{k}</div>
                                    <div className="border-b border-dashed border-stone-200 pb-1 text-ink">{v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                {/* Right: embedded WhatsApp component */}
                <div className="lg:col-span-2">
                    <div className="overflow-hidden rounded-lg border border-stone-200 bg-white shadow-sm">
                        <div className="flex items-center gap-2 border-b border-stone-100 bg-emerald-600 px-3 py-2 text-white">
                            <WhatsappLogo size={16} weight="fill" /> <span className="text-xs font-bold uppercase tracking-wider">WhatsApp</span>
                            <span className="ml-auto text-[10px] opacity-80">+91 98104 12345</span>
                        </div>
                        <div className="space-y-2 p-3" style={{ background: "#efeae2" }}>
                            <div className="flex justify-start"><div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-white px-3 py-1.5 text-xs shadow-sm">Hi, is the July order confirmed?</div></div>
                            <div className="flex justify-end"><div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-emerald-100 px-3 py-1.5 text-xs shadow-sm">Yes Priya! Dispatching tomorrow 🚚 <span className="ml-1 text-[9px] text-stone-500">✓✓</span></div></div>
                            <div className="flex justify-start"><div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-white px-3 py-1.5 text-xs shadow-sm">Perfect, thank you!</div></div>
                        </div>
                        <div className="flex items-center gap-2 border-t border-stone-100 p-2">
                            <div className="flex-1 rounded-full border border-stone-200 px-3 py-1.5 text-[11px] text-stone-400">Type a message…</div>
                            <div className="grid h-7 w-7 place-items-center rounded-full bg-emerald-600 text-white"><PaperPlaneTilt size={12} weight="fill" /></div>
                        </div>
                    </div>
                    <p className="mt-2 text-center text-[11px] text-stone2">Preview — how the component looks embedded on a Lead record page.</p>
                </div>
            </div>
        </div>
    );
}

function SalesforceApi() {
    const [cfg, setCfg] = useState(null);
    const [newKey, setNewKey] = useState("");
    const [codeTab, setCodeTab] = useState("js");
    const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

    const load = async () => { const { data } = await api.get("/whatsapp/settings"); setCfg(data); };
    useEffect(() => { load(); }, []);

    const rotate = async () => {
        try { const { data } = await api.post("/whatsapp/api-key"); setNewKey(data.api_key); toast.success("API key generated — copy it now"); await load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    if (!cfg) return <div className="p-8 text-sm text-stone2">Loading…</div>;
    const code = CODE_FILES[codeTab] || CODE_FILES.js;

    return (
        <div className="space-y-6">
            {/* Preview hero */}
            <div>
                <div className="mb-3 flex items-center gap-2">
                    <Eye size={18} className="text-emerald-600" />
                    <h3 className="font-heading text-lg font-bold">See it on a Salesforce Lead page</h3>
                </div>
                <LeadPagePreview />
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
                    <div className="mb-1 flex items-center gap-2"><Key size={18} className="text-emerald-600" /><h3 className="font-heading text-lg font-bold">Workspace API key</h3></div>
                    <p className="mb-4 text-sm text-stone2">Your Salesforce Apex/LWC uses this key (header <code className="font-mono">X-API-Key</code>) to send &amp; read WhatsApp messages.</p>
                    {cfg.has_api_key ? (
                        <div className="mb-4 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-sm">Active key: <code data-testid="wa-key-prefix" className="font-mono">{cfg.api_key_prefix}…</code></div>
                    ) : <p className="mb-4 text-sm text-amber-600">No API key yet.</p>}
                    {newKey && (
                        <div className="mb-4">
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-emerald-700">New key (shown once)</label>
                            <CopyField value={newKey} testid="wa-new-key" />
                        </div>
                    )}
                    <button data-testid="wa-rotate-key" onClick={rotate} className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5">
                        {cfg.has_api_key ? "Regenerate key" : "Generate API key"}
                    </button>
                </div>

                <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
                    <h3 className="mb-3 font-heading text-lg font-bold">Endpoints for Salesforce</h3>
                    <div className="space-y-4 text-sm">
                        <div>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone2">Send message</div>
                            <CopyField value={`POST ${backend}/api/whatsapp/external/send`} testid="wa-ep-send" />
                            <pre className="mt-2 overflow-x-auto rounded-lg bg-stone-900 p-3 text-[11px] text-stone-100">{`{ "to": "9198…", "type": "text", "body": "Hi!" }`}</pre>
                        </div>
                        <div>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone2">Send media / attachment</div>
                            <CopyField value={`POST ${backend}/api/whatsapp/external/send-media`} testid="wa-ep-send-media" />
                            <pre className="mt-2 overflow-x-auto rounded-lg bg-stone-900 p-3 text-[11px] text-stone-100">{`{ "to": "9198…", "filename": "brochure.pdf", "content_type": "application/pdf", "data_base64": "…", "caption": "Details" }`}</pre>
                        </div>
                        <div><div className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone2">Fetch messages for a contact <span className="normal-case text-stone2">(add <code className="font-mono">&amp;since=&lt;iso-ts&gt;</code> for delta polling)</span></div>
                            <CopyField value={`GET ${backend}/api/whatsapp/external/messages?contact=9198…`} testid="wa-ep-messages" /></div>
                        <div><div className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone2">Render inbound media <span className="normal-case text-stone2">(base64 for Apex; raw at /media/&lt;ref&gt;)</span></div>
                            <CopyField value={`GET ${backend}/api/whatsapp/external/media-base64/{ref}`} testid="wa-ep-media" /></div>
                        <div><div className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone2">List message templates</div>
                            <CopyField value={`GET ${backend}/api/whatsapp/external/templates`} testid="wa-ep-templates" /></div>
                        <div><div className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone2">Session window state <span className="normal-case text-stone2">(24h open/closed)</span></div>
                            <CopyField value={`GET ${backend}/api/whatsapp/external/session?contact=9198…`} testid="wa-ep-session" /></div>
                        <div><div className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone2">Mark inbound read</div>
                            <CopyField value={`POST ${backend}/api/whatsapp/external/mark-read`} testid="wa-ep-mark-read" />
                            <pre className="mt-2 overflow-x-auto rounded-lg bg-stone-900 p-3 text-[11px] text-stone-100">{`{ "contact": "9198…" }`}</pre>
                        </div>
                        <div><div className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone2">List conversations</div>
                            <CopyField value={`GET ${backend}/api/whatsapp/external/conversations`} testid="wa-ep-convos" /></div>
                        <p className="text-xs text-stone2">All requests include header <code className="font-mono">X-API-Key</code>. Sends also pass <code className="font-mono">X-Agent-Email</code> so messages are attributed to the right agent.</p>
                    </div>
                </div>
            </div>

            {/* Sample code viewer */}
            <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-2"><Code size={18} className="text-emerald-600" /><h3 className="font-heading text-lg font-bold">Ready-to-deploy sample</h3></div>
                    <div className="flex items-center gap-3">
                        <div className="flex gap-1 rounded-lg border border-stone-200 bg-stone-100 p-1">
                            {[["js", "LWC JS"], ["html", "LWC HTML"], ["css", "LWC CSS"], ["meta", "Meta XML"], ["apex", "Apex"], ["apexmeta", "Apex Meta"]].map(([k, l]) => (
                                <button key={k} data-testid={`wa-code-tab-${k}`} onClick={() => setCodeTab(k)}
                                    className={`rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all duration-200 ${codeTab === k ? "bg-white text-ink shadow-sm" : "text-stone2 hover:text-ink"}`}>{l}</button>
                            ))}
                        </div>
                        <button data-testid="wa-code-copy" onClick={() => { navigator.clipboard?.writeText(code); toast.success("Code copied"); }}
                            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3.5 py-2 text-xs font-semibold text-stone2 transition-all duration-200 hover:border-ink hover:text-ink">
                            <Copy size={14} /> Copy
                        </button>
                    </div>
                </div>
                <pre className="max-h-[360px] overflow-auto rounded-xl bg-stone-900 p-4 text-[12px] leading-relaxed text-stone-100" data-testid="wa-code-block"><code>{code}</code></pre>
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-emerald-100 bg-emerald-50/50 p-3 text-xs text-stone2">
                    <Envelope size={15} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span>This is the <b>full, current</b> sample — the LWC bundle is <b>5 files</b> (JS + HTML + CSS + meta.xml) plus the Apex class. The <b>Apex</b> you can paste into Setup → Apex Classes, but an <b>LWC bundle cannot be created by paste in Setup</b> — deploy it via SF CLI (<code className="font-mono">sf project deploy start -d classes -d lwc</code>) or VS Code. Same files also live at <code className="font-mono">/app/salesforce_sample</code>. Then drop the <b>Inspiro WhatsApp</b> component onto any Lead/Contact page. ⚠️ Deploy the <b>Apex and LWC together</b> — a mismatch (LWC importing a method the Apex lacks) renders the component blank.</span>
                </div>
            </div>
        </div>
    );
}

/* ---------------- Bot / Auto-Reply flow builder ---------------- */
const COMMON_OBJECTS = ["Lead", "Case", "Contact", "Opportunity"];
const uid = () => Math.random().toString(36).slice(2, 9);

function LeafEditor({ option, onOption, onFields, objectOptions, fieldCache, loadFields, p, inputCls, labelCls, sfConnected }) {
    const fields = fieldCache[option.sf_object] || [];
    const [manualObj, setManualObj] = useState(
        !!option.sf_object && sfConnected && !["Lead", "Case", ...(objectOptions || [])].includes(option.sf_object)
    );
    const addField = () => onFields([...(option.fields || []), { _k: uid(), prompt: "", sf_field: "" }]);
    const setField = (fi, patch) => onFields((option.fields || []).map((f, i) => (i === fi ? { ...f, ...patch } : f)));
    const removeField = (fi) => onFields((option.fields || []).filter((_, i) => i !== fi));

    if (option.action === "escalate") {
        return (
            <div className="rounded-lg border border-stone-200 bg-white p-3">
                <label className={labelCls}>Escalation message</label>
                <input data-testid={`${p}-escalate`} className={inputCls} value={option.escalate_message || ""} onChange={(e) => onOption({ escalate_message: e.target.value })} placeholder="Connecting you to an expert — someone will reply shortly." />
                <p className="mt-1.5 text-xs text-stone2">The conversation is handed to your team in the Inbox; the bot stays quiet until the customer types a trigger keyword.</p>
            </div>
        );
    }
    const objChoices = sfConnected ? objectOptions : ["Lead", "Case"];
    const targetLabel = option.sf_object || "Lead";
    const QUICK = [
        { chip: "Full name", prompt: "What's your full name?", sf: sfConnected ? "LastName" : "Full Name" },
        { chip: "Email", prompt: "What's your email address?", sf: "Email" },
        { chip: "Phone", prompt: "What's the best phone number to reach you?", sf: "Phone" },
        { chip: "Product / Model", prompt: "Which model are you interested in?", sf: sfConnected ? "" : "Model" },
        { chip: "City", prompt: "Which city are you in?", sf: "City" },
    ];
    const quickAdd = (q) => onFields([...(option.fields || []), { _k: uid(), prompt: q.prompt, sf_field: q.sf }]);
    return (
        <div className="space-y-3 rounded-lg border border-stone-200 bg-white p-3">
            <div className="flex items-start gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800" data-testid={`${p}-collect-help`}>
                <Info size={14} className="mt-0.5 shrink-0" />
                <span>The bot asks each question below <b>one at a time</b>, and the customer <b>replies in plain text</b> (e.g. the model, their name, their email). After the last answer, a <b>{targetLabel}</b> is captured{sfConnected ? " in Salesforce" : " under the Leads tab"}.</span>
            </div>

            {/* Questions FIRST — the free-text conversation the bot has with the customer */}
            <div>
                <div className="mb-1.5 flex items-center justify-between">
                    <label className={labelCls + " mb-0"}>Questions the bot asks (in order)</label>
                    <button data-testid={`${p}-add-field`} onClick={addField} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700"><PlusCircle size={14} /> Add question</button>
                </div>
                <div className="mb-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] text-stone2">Quick add:</span>
                    {QUICK.map((q) => (
                        <button key={q.chip} data-testid={`${p}-quick-${q.chip.split(" ")[0].toLowerCase()}`} onClick={() => quickAdd(q)}
                            className="rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[11px] font-medium text-stone-600 transition-colors hover:border-emerald-400 hover:text-emerald-700">+ {q.chip}</button>
                    ))}
                </div>
                {(option.fields || []).length === 0 && (
                    <p className="rounded-lg border border-dashed border-stone-300 bg-stone-50 px-3 py-2 text-xs text-stone2">No questions yet — add one above (the customer types their reply). With no questions, the {targetLabel} is created immediately.</p>
                )}
                <div className="space-y-2">
                    {(option.fields || []).map((f, fi) => (
                        <div key={f._k} className="rounded-lg border border-stone-200 bg-stone-50/60 p-2.5" data-testid={`${p}-field-${fi}`}>
                            <div className="flex items-center gap-2">
                                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-emerald-100 text-[10px] font-bold text-emerald-700">{fi + 1}</span>
                                <input data-testid={`${p}-field-prompt-${fi}`} className={`${inputCls} flex-1`} value={f.prompt} onChange={(e) => setField(fi, { prompt: e.target.value })} placeholder="Question the bot sends (e.g. Which model are you interested in?)" />
                                <button data-testid={`${p}-field-remove-${fi}`} onClick={() => removeField(fi)} className="shrink-0 rounded-lg border border-stone-300 p-2 text-stone2 hover:border-red-400 hover:text-red-500"><X size={13} /></button>
                            </div>
                            <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-7 text-[11px] text-stone2">
                                <span className="italic">↳ Customer replies in text</span>
                                <span className="text-stone-300">·</span>
                                <span>Save answer as{sfConnected ? "" : " (optional)"}:</span>
                                {sfConnected && fields.length ? (
                                    <select data-testid={`${p}-field-sf-${fi}`} className="rounded-md border border-stone-300 px-2 py-1 text-[11px] focus:border-emerald-500 focus:outline-none" value={f.sf_field} onChange={(e) => setField(fi, { sf_field: e.target.value })} title="Salesforce field">
                                        <option value="">— don't store (optional) —</option>
                                        {fields.map((sf) => <option key={sf.name} value={sf.name}>{sf.label} ({sf.name})</option>)}
                                    </select>
                                ) : (
                                    <input data-testid={`${p}-field-sf-${fi}`} className="w-44 rounded-md border border-stone-300 px-2 py-1 text-[11px] focus:border-emerald-500 focus:outline-none" value={f.sf_field} onChange={(e) => setField(fi, { sf_field: e.target.value })} placeholder={sfConnected ? "Salesforce field API name" : "Label e.g. Model"} title={sfConnected ? "Salesforce field API name" : "Column name in Captured Leads / CSV"} />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Where the captured lead goes — secondary */}
            <div className="grid gap-3 border-t border-stone-100 pt-3 md:grid-cols-2">
                <div>
                    <label className={labelCls}>{sfConnected ? "Create record in" : "Save lead as"}</label>
                    {sfConnected && manualObj ? (
                        <div className="flex items-center gap-2">
                            <input data-testid={`${p}-object-custom`} className={inputCls} value={option.sf_object || ""}
                                onChange={(e) => onOption({ sf_object: e.target.value.trim() })}
                                onBlur={(e) => loadFields(e.target.value.trim())}
                                placeholder="Custom object API name e.g. Vehicle_Service__c" />
                            <button type="button" data-testid={`${p}-object-pick-list`} onClick={() => setManualObj(false)}
                                className="shrink-0 rounded-lg border border-stone-300 px-2.5 py-2 text-[11px] font-semibold text-stone2 hover:border-ink hover:text-ink">List</button>
                        </div>
                    ) : (
                        <select data-testid={`${p}-object`} className={inputCls} value={option.sf_object || "Lead"}
                            onChange={(e) => { if (e.target.value === "__other__") { setManualObj(true); return; } onOption({ sf_object: e.target.value }); loadFields(e.target.value); }}>
                            {sfConnected && <option value="__other__">✎ Other — type a custom object API name…</option>}
                            {sfConnected && option.sf_object && !objChoices.includes(option.sf_object) && <option value={option.sf_object}>{option.sf_object}</option>}
                            {sfConnected ? (
                                <>
                                    <optgroup label="Your custom objects">
                                        {objChoices.filter((n) => n.includes("__")).map((n) => <option key={n} value={n}>{n}</option>)}
                                    </optgroup>
                                    <optgroup label="Standard objects">
                                        {objChoices.filter((n) => !n.includes("__")).map((n) => <option key={n} value={n}>{n}</option>)}
                                    </optgroup>
                                </>
                            ) : (
                                objChoices.map((n) => <option key={n} value={n}>{n}</option>)
                            )}
                        </select>
                    )}
                    {sfConnected && <p className="mt-1 text-[10px] text-stone2">For a custom object, pick it from the list (only createable objects are shown) or choose “Other” and type its API name, e.g. <b>Vehicle_Service__c</b>.</p>}
                </div>
                <div>
                    <label className={labelCls}>Confirmation message</label>
                    <input data-testid={`${p}-done`} className={inputCls} value={option.completion_message || ""} onChange={(e) => onOption({ completion_message: e.target.value })} placeholder="Thank you! Our team will reach out." />
                </div>
            </div>
            {!sfConnected && (
                <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-800" data-testid={`${p}-nocrm-note`}>
                    <Info size={14} className="mt-0.5 shrink-0" />
                    <span>Salesforce isn’t connected, so answers are saved as <b>Captured Leads</b> (view &amp; export from the “Leads” tab). Connect Salesforce to also auto-create records with proper field mapping.</span>
                </div>
            )}
        </div>
    );
}

function BotAnalytics() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get(`/whatsapp/bot/analytics?days=${days}`).then(({ data }) => { if (alive) setData(data); })
            .catch(() => { }).finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [days]);

    const t = data?.totals || {};
    const cards = [
        { key: "conversations", label: "Conversations", value: t.conversations ?? 0, icon: ChatCircleDots },
        { key: "records", label: "Leads captured", value: t.records_created ?? 0, icon: CheckCircle },
        { key: "success", label: "Success rate", value: t.success_rate == null ? "—" : `${t.success_rate}%`, icon: Checks },
        { key: "escalations", label: "Escalations", value: t.escalations ?? 0, icon: User },
    ];
    return (
        <div className="space-y-6" data-testid="wa-bot-analytics">
            <div className="flex items-center justify-between">
                <h4 className="font-heading text-lg font-bold">Bot performance</h4>
                <div className="flex gap-1 rounded-lg border border-stone-200 bg-stone-100 p-1">
                    {[[7, "7d"], [30, "30d"], [90, "90d"]].map(([d, l]) => (
                        <button key={d} data-testid={`wa-bot-analytics-days-${d}`} onClick={() => setDays(d)}
                            className={`rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all duration-200 ${days === d ? "bg-white text-ink shadow-sm" : "text-stone2 hover:text-ink"}`}>{l}</button>
                    ))}
                </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {cards.map((c) => (
                    <div key={c.key} data-testid={`wa-bot-stat-${c.key}`} className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                        <div className="flex items-center gap-2 text-stone2"><c.icon size={16} className="text-emerald-600" /><span className="text-xs font-semibold uppercase tracking-wide">{c.label}</span></div>
                        <div className="mt-2 font-heading text-3xl font-bold">{loading ? "…" : c.value}</div>
                    </div>
                ))}
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
                    <h5 className="mb-3 font-heading font-bold">By menu option</h5>
                    {(!data?.per_option || data.per_option.length === 0) ? (
                        <p className="text-sm text-stone2">{loading ? "Loading…" : "No selections yet in this period."}</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-stone-100 text-left text-xs uppercase tracking-wide text-stone2">
                                <th className="py-2">Option</th><th className="py-2 text-right">Chosen</th><th className="py-2 text-right">Records</th><th className="py-2 text-right">Escalations</th>
                            </tr></thead>
                            <tbody>
                                {data.per_option.map((o) => (
                                    <tr key={o.option_id} data-testid={`wa-bot-analytics-row-${o.option_id}`} className="border-b border-stone-50 last:border-0">
                                        <td className="py-2 font-medium text-ink">{o.label}</td>
                                        <td className="py-2 text-right">{o.selections}</td>
                                        <td className="py-2 text-right">{o.records_created}</td>
                                        <td className="py-2 text-right">{o.escalations}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
                    <h5 className="mb-3 font-heading font-bold">Recent activity</h5>
                    {(!data?.recent || data.recent.length === 0) ? (
                        <p className="text-sm text-stone2">{loading ? "Loading…" : "No activity yet."}</p>
                    ) : (
                        <ul className="space-y-2">
                            {data.recent.map((r, i) => (
                                <li key={i} className="flex items-center justify-between gap-3 border-b border-stone-50 pb-2 text-sm last:border-0">
                                    <span className="flex items-center gap-2">
                                        {r.type === "record_created" && <CheckCircle size={15} className="text-emerald-600" weight="fill" />}
                                        {r.type === "lead_captured" && <CheckCircle size={15} className="text-emerald-600" weight="fill" />}
                                        {r.type === "record_failed" && <X size={15} className="text-red-500" />}
                                        {r.type === "escalated" && <User size={15} className="text-amber-500" />}
                                        {r.type === "conversation_started" && <ChatCircleDots size={15} className="text-sky-500" />}
                                        <span className="text-ink">
                                            {r.type === "record_created" && `Captured ${r.sf_object || "record"} · ${r.label || ""}`}
                                            {r.type === "lead_captured" && `Captured ${r.sf_object || "lead"} · ${r.label || ""}`}
                                            {r.type === "record_failed" && `Record failed · ${r.label || ""}`}
                                            {r.type === "escalated" && `Escalated · ${r.label || ""}`}
                                            {r.type === "conversation_started" && "New conversation"}
                                        </span>
                                    </span>
                                    <span className="shrink-0 text-xs text-stone2">{r.contact}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function BotTester() {
    const [chat, setChat] = useState([]); // {role:'user'|'bot'|'system', ...}
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [state, setState] = useState(null);
    const endRef = useRef(null);

    const scrollDown = () => setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 60);

    const call = async (payload, userBubble) => {
        setBusy(true);
        try {
            const next = userBubble ? [...chat, { role: "user", type: "text", body: userBubble }] : chat;
            if (userBubble) setChat(next);
            const { data } = await api.post("/whatsapp/bot/simulate", payload);
            setChat([...(payload.reset ? [] : next), ...(userBubble && payload.reset ? [{ role: "user", type: "text", body: userBubble }] : []), ...data.messages]);
            setState(data.state);
            scrollDown();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Simulation failed"); }
        finally { setBusy(false); }
    };

    const start = () => call({ message: "hi", reset: true }, "hi");
    useEffect(() => { start(); /* eslint-disable-next-line */ }, []);

    const tapRow = (row) => call({ interactive_id: row.id }, row.title);
    const send = () => { const t = input.trim(); if (!t) return; setInput(""); call({ message: t }, t); };

    return (
        <div className="mx-auto max-w-md" data-testid="wa-bot-tester">
            <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-stone2">
                    <Robot size={14} className="text-emerald-600" weight="fill" /> Live bot test
                </div>
                <button data-testid="wa-bot-test-restart" onClick={start} disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-stone2 transition-all duration-200 hover:border-ink hover:text-ink disabled:opacity-50">
                    <ArrowClockwise size={13} /> Restart
                </button>
            </div>
            <div className="overflow-hidden rounded-[2rem] border-4 border-stone-800 bg-stone-800 shadow-xl">
                <div className="flex items-center gap-2 bg-emerald-700 px-4 py-3 text-white">
                    <div className="grid h-8 w-8 place-items-center rounded-full bg-white/20"><WhatsappLogo size={18} weight="fill" /></div>
                    <div><div className="text-sm font-semibold">Your Business</div><div className="text-[10px] text-emerald-100">bot preview</div></div>
                </div>
                <div className="space-y-2 overflow-y-auto px-3 py-4" style={{ backgroundColor: "#ECE5DD", height: 420 }}>
                    {chat.map((m, i) => {
                        if (m.role === "user") {
                            return <div key={i} className="ml-auto max-w-[80%] rounded-xl rounded-tr-sm bg-[#DCF8C6] px-3 py-2 text-[13px] text-stone-800 shadow-sm">{m.body}</div>;
                        }
                        if (m.type === "text") {
                            return <div key={i} className="max-w-[80%] whitespace-pre-wrap break-words rounded-xl rounded-tl-sm bg-white px-3 py-2 text-[13px] text-stone-800 shadow-sm">{m.body}</div>;
                        }
                        if (m.type === "image") {
                            return <div key={i} className="max-w-[80%] rounded-xl rounded-tl-sm bg-white p-1 shadow-sm"><img src={`${API_BASE}/whatsapp/media/${m.ref}`} alt="" className="rounded-lg" /></div>;
                        }
                        if (m.type === "menu") {
                            return (
                                <div key={i} className="max-w-[88%] overflow-hidden rounded-xl rounded-tl-sm bg-white text-[13px] text-stone-800 shadow-sm">
                                    <div className="px-3 pt-2.5">
                                        {m.header && <div className="font-bold">{m.header}</div>}
                                        {m.body && <div className="mt-0.5 text-stone-600">{m.body}</div>}
                                    </div>
                                    <div className="mt-2 divide-y divide-stone-100 border-t border-stone-100">
                                        {m.rows.map((r) => (
                                            <button key={r.id} data-testid={`wa-bot-test-row-${r.id}`} onClick={() => tapRow(r)} disabled={busy}
                                                className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors hover:bg-emerald-50 disabled:opacity-60">
                                                <span className="min-w-0"><span className="block truncate font-medium text-sky-700">{r.title}</span>{r.description && <span className="block truncate text-[11px] text-stone-500">{r.description}</span>}</span>
                                                <span className="text-stone-400">{r.action === "submenu" ? "›" : "→"}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        }
                        if (m.type === "record") {
                            return <div key={i} className="mx-auto max-w-[92%] rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800"><b>✓ {m.sobject === "Case" ? "Case" : "Lead"} captured{m.synced ? " · will sync to your CRM" : ""}</b><div className="mt-0.5 text-emerald-700">{Object.entries(m.fields || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div></div>;
                        }
                        return null;
                    })}
                    {(state === "done" || state === "escalated") && (
                        <div className="mx-auto rounded-full bg-stone-200 px-3 py-1 text-[11px] font-semibold text-stone-600">Conversation {state} — tap Restart to test again</div>
                    )}
                    <div ref={endRef} />
                </div>
                <div className="flex items-center gap-2 bg-stone-100 px-3 py-2">
                    <input data-testid="wa-bot-test-input" value={input} onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Type a reply…" disabled={busy}
                        className="flex-1 rounded-full border border-stone-300 bg-white px-4 py-2 text-sm outline-none focus:border-emerald-500" />
                    <button data-testid="wa-bot-test-send" onClick={send} disabled={busy || !input.trim()}
                        className="grid h-9 w-9 place-items-center rounded-full bg-emerald-600 text-white transition-all hover:bg-emerald-700 disabled:opacity-50"><PaperPlaneTilt size={16} weight="fill" /></button>
                </div>
            </div>
            <p className="mt-2 text-center text-[11px] text-stone2">Simulated — no real WhatsApp message is sent and no lead/case is saved.</p>
        </div>
    );
}

function BotSubmissions() {
    const { orgTimezone } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dl, setDl] = useState(false);
    useEffect(() => {
        let alive = true;
        api.get("/whatsapp/bot/submissions?days=90").then(({ data }) => { if (alive) setData(data); })
            .catch(() => { }).finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const download = async () => {
        setDl(true);
        try {
            const res = await api.get("/whatsapp/bot/submissions.csv?days=90", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url; a.download = "captured-leads.csv"; document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (_e) { toast.error("Couldn't export CSV"); } finally { setDl(false); }
    };

    const rows = data?.submissions || [];
    const sfOn = data?.salesforce_connected;
    return (
        <div className="space-y-4" data-testid="wa-bot-submissions">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h4 className="font-heading text-lg font-bold">Captured leads</h4>
                    <p className="mt-0.5 text-sm text-stone2">
                        Every completed bot conversation, saved in Inspiro{sfOn ? " and synced to your CRM" : " — connect Salesforce to also auto-create records"}. Last 90 days.
                    </p>
                </div>
                <button data-testid="wa-leads-export" onClick={download} disabled={dl || rows.length === 0}
                    className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50">
                    {dl ? <CircleNotch size={15} className="animate-spin" /> : <FileArrowDown size={15} />} Download CSV
                </button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                {loading ? (
                    <p className="p-6 text-sm text-stone2">Loading…</p>
                ) : rows.length === 0 ? (
                    <p className="p-6 text-sm text-stone2">No captured leads yet. When a customer completes a bot flow, it appears here.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-stone-100 bg-stone-50 text-left text-xs uppercase tracking-wide text-stone2">
                                <th className="px-4 py-2.5">When</th><th className="px-4 py-2.5">Contact</th><th className="px-4 py-2.5">Option</th>
                                <th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Details</th><th className="px-4 py-2.5">Status</th>
                            </tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} data-testid={`wa-lead-row-${r.id}`} className="border-b border-stone-50 align-top last:border-0">
                                        <td className="whitespace-nowrap px-4 py-3 text-xs text-stone2">{formatDateTime(r.created_at, orgTimezone)}</td>
                                        <td className="whitespace-nowrap px-4 py-3 font-medium text-ink">+{r.contact}</td>
                                        <td className="px-4 py-3">{r.label}</td>
                                        <td className="px-4 py-3">{r.sobject}</td>
                                        <td className="px-4 py-3 text-stone-600">{Object.entries(r.fields || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}</td>
                                        <td className="whitespace-nowrap px-4 py-3">
                                            {r.synced
                                                ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700"><Check size={10} weight="bold" /> Synced to CRM</span>
                                                : <span className="inline-flex items-center gap-1 rounded-full bg-stone-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-stone-600">Saved in Inspiro</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function BotBuilder() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [sobjects, setSobjects] = useState([]);
    const [fieldCache, setFieldCache] = useState({});
    const [sfOk, setSfOk] = useState(true);
    const [view, setView] = useState("builder");

    const load = async () => {
        const { data } = await api.get("/whatsapp/bot");
        const withKeys = (o) => ({
            ...o, _k: uid(),
            fields: (o.fields || []).map((f) => ({ ...f, _k: uid() })),
            children: (o.children || []).map(withKeys),
        });
        data.options = (data.options || []).map(withKeys);
        setCfg(data);
    };
    const loadSobjects = async () => {
        try { const { data } = await api.get("/integrations/salesforce/sobjects"); setSobjects(data.sobjects || []); }
        catch (_e) { setSfOk(false); }
    };
    const loadFields = async (obj) => {
        if (!obj || fieldCache[obj]) return;
        try { const { data } = await api.get(`/integrations/salesforce/describe/${obj}`); setFieldCache((p) => ({ ...p, [obj]: data.fields || [] })); }
        catch (_e) { setFieldCache((p) => ({ ...p, [obj]: [] })); }
    };

    useEffect(() => { load(); loadSobjects(); }, []);
    useEffect(() => {
        const walk = (o) => {
            if (o.action === "collect" && o.sf_object) loadFields(o.sf_object);
            (o.children || []).forEach(walk);
        };
        (cfg?.options || []).forEach(walk);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify((cfg?.options || []).map((o) => [o.sf_object, (o.children || []).map((c) => c.sf_object)]))]);

    const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
    const setOpt = (idx, patch) => setCfg((c) => {
        const options = c.options.slice(); options[idx] = { ...options[idx], ...patch }; return { ...c, options };
    });
    const addOpt = () => setCfg((c) => ({
        ...c, options: [...c.options, {
            _k: uid(), label: "", description: "", action: "collect", sf_object: "Lead",
            fields: [], children: [], completion_message: "Thank you! Our team will get back to you shortly.",
            escalate_message: "Connecting you with a specialist — someone will reply here shortly.",
        }],
    }));
    const removeOpt = (idx) => setCfg((c) => ({ ...c, options: c.options.filter((_, i) => i !== idx) }));
    const moveOpt = (idx, dir) => setCfg((c) => {
        const j = idx + dir; if (j < 0 || j >= c.options.length) return c;
        const options = c.options.slice(); [options[idx], options[j]] = [options[j], options[idx]]; return { ...c, options };
    });

    // Sub-menu child helpers
    const setChild = (oi, ci, patch) => setCfg((c) => {
        const options = c.options.slice();
        const children = (options[oi].children || []).slice();
        children[ci] = { ...children[ci], ...patch };
        options[oi] = { ...options[oi], children }; return { ...c, options };
    });
    const addChild = (oi) => setCfg((c) => {
        const options = c.options.slice();
        const children = [...(options[oi].children || []), {
            _k: uid(), label: "", description: "", action: "collect", sf_object: "Lead", fields: [],
            completion_message: "Thank you! Our team will get back to you shortly.",
            escalate_message: "Connecting you with a specialist — someone will reply here shortly.",
        }];
        options[oi] = { ...options[oi], children }; return { ...c, options };
    });
    const removeChild = (oi, ci) => setCfg((c) => {
        const options = c.options.slice();
        options[oi] = { ...options[oi], children: (options[oi].children || []).filter((_, i) => i !== ci) };
        return { ...c, options };
    });

    const imgRef = useRef(null);
    const [uploadingImg, setUploadingImg] = useState(false);
    const onPickImage = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUploadingImg(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/whatsapp/bot/welcome-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            set({ welcome_image_ref: data.media_ref, welcome_image_mime: data.mime });
            toast.success("Welcome image added");
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed"); }
        finally { setUploadingImg(false); }
    };
    const removeImage = async () => {
        try { await api.delete("/whatsapp/bot/welcome-image"); } catch (_e) { /* ignore */ }
        set({ welcome_image_ref: null, welcome_image_mime: null });
    };

    const buildPayload = (overrides = {}) => {
        const strip = (o) => ({
            ...o, _k: undefined,
            fields: (o.fields || []).map((f) => ({ prompt: f.prompt, sf_field: f.sf_field })),
            children: (o.children || []).map(strip),
        });
        return { ...cfg, ...overrides, options: (cfg.options || []).map(strip) };
    };
    const applySaved = (data) => {
        const withKeys = (o) => ({ ...o, _k: uid(), fields: (o.fields || []).map((f) => ({ ...f, _k: uid() })), children: (o.children || []).map(withKeys) });
        data.options = (data.options || []).map(withKeys);
        setCfg(data);
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/whatsapp/bot", buildPayload());
            applySaved(data);
            toast.success("Bot flow saved");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't save"); }
        finally { setSaving(false); }
    };

    // The header on/off switch persists immediately (independent of the Save button)
    // so it survives a page refresh.
    const [togglingEnabled, setTogglingEnabled] = useState(false);
    const toggleEnabled = async () => {
        if (togglingEnabled) return;
        const next = !cfg.enabled;
        setTogglingEnabled(true);
        set({ enabled: next }); // optimistic
        try {
            const { data } = await api.put("/whatsapp/bot", buildPayload({ enabled: next }));
            applySaved(data);
            toast.success(next ? "Auto-Reply Bot enabled" : "Auto-Reply Bot disabled");
        } catch (e) {
            set({ enabled: !next }); // revert on failure
            toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't update the bot");
        } finally { setTogglingEnabled(false); }
    };

    if (!cfg) return <div className="p-8 text-sm text-stone2">Loading…</div>;

    const objectOptions = (sobjects.length ? sobjects.map((s) => s.name) : COMMON_OBJECTS);
    const inputCls = "w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100";
    const labelCls = "mb-1 block text-xs font-semibold uppercase tracking-wide text-stone2";

    return (
        <div className="space-y-6" data-testid="wa-bot-builder">
            {/* Header + enable */}
            <div className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-stone-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center">
                <div>
                    <div className="flex items-center gap-2"><Robot size={20} className="text-emerald-600" weight="fill" /><h3 className="font-heading text-lg font-bold">Auto-Reply Bot</h3></div>
                    <p className="mt-1 max-w-2xl text-sm text-stone2">When a customer messages your WhatsApp number, the bot greets them, shows a menu, collects their details step-by-step, and creates a Salesforce record automatically.</p>
                </div>
                <button
                    data-testid="wa-bot-enable-toggle"
                    role="switch"
                    aria-checked={cfg.enabled}
                    disabled={togglingEnabled}
                    data-state={cfg.enabled ? "checked" : "unchecked"}
                    onClick={toggleEnabled}
                    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 disabled:opacity-60 ${cfg.enabled ? "bg-emerald-500" : "bg-stone-300"}`}
                >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${cfg.enabled ? "translate-x-6" : "translate-x-1"}`} />
                </button>
            </div>

            <div className="flex w-fit gap-1 rounded-xl border border-stone-200 bg-stone-100 p-1">
                {[["builder", "Builder"], ["test", "Test"], ["leads", "Leads"], ["analytics", "Analytics"]].map(([k, l]) => (
                    <button key={k} data-testid={`wa-bot-view-${k}`} onClick={() => setView(k)}
                        className={`rounded-lg px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-200 ${view === k ? "bg-white text-ink shadow-sm" : "text-stone2 hover:text-ink"}`}>{l}</button>
                ))}
            </div>

            {view === "test" && <BotTester />}
            {view === "leads" && <BotSubmissions />}
            {view === "analytics" && <BotAnalytics />}

            {view === "builder" && (<>
            {!sfOk && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700" data-testid="wa-bot-sf-warning">
                    Salesforce isn't connected — the bot will still run and collect answers, but records can't be created until you connect Salesforce in Integrations. Field names below fall back to free text.
                </div>
            )}

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
                <div className="min-w-0 space-y-6">
            {/* Welcome + menu */}
            <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center gap-2"><ChatCircleDots size={18} className="text-emerald-600" /><h4 className="font-heading font-bold">Greeting &amp; menu</h4></div>
                <div className="grid gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                        <label className={labelCls}>Welcome message</label>
                        <textarea data-testid="wa-bot-welcome" rows={2} className={inputCls} value={cfg.welcome_message || ""} onChange={(e) => set({ welcome_message: e.target.value })} placeholder="👋 Welcome to Anvil Energy" />
                    </div>
                    <div className="md:col-span-2">
                        <label className={labelCls}>Welcome image <span className="normal-case text-stone2">(optional — sent before the menu, e.g. your logo/hero)</span></label>
                        <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} data-testid="wa-bot-image-input" />
                        {cfg.welcome_image_ref ? (
                            <div className="flex items-center gap-3">
                                <img src={`${API_BASE}/whatsapp/media/${cfg.welcome_image_ref}`} alt="welcome" className="h-16 w-16 rounded-lg border border-stone-200 object-cover" data-testid="wa-bot-image-thumb" />
                                <button data-testid="wa-bot-image-replace" onClick={() => imgRef.current?.click()} disabled={uploadingImg}
                                    className="rounded-full border border-stone-300 bg-white px-3.5 py-2 text-xs font-semibold text-stone2 transition-all duration-200 hover:border-ink hover:text-ink disabled:opacity-50">Replace</button>
                                <button data-testid="wa-bot-image-remove" onClick={removeImage}
                                    className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-3.5 py-2 text-xs font-semibold text-stone2 transition-all duration-200 hover:border-red-400 hover:text-red-500"><Trash size={13} /> Remove</button>
                            </div>
                        ) : (
                            <button data-testid="wa-bot-image-upload" onClick={() => imgRef.current?.click()} disabled={uploadingImg}
                                className="inline-flex items-center gap-2 rounded-lg border border-dashed border-stone-300 bg-stone-50 px-4 py-3 text-sm font-semibold text-stone2 transition-all duration-200 hover:border-emerald-400 hover:text-emerald-600 disabled:opacity-50">
                                {uploadingImg ? <CircleNotch size={16} className="animate-spin" /> : <Paperclip size={16} />}
                                {uploadingImg ? "Uploading…" : "Upload image (JPG/PNG, ≤5MB)"}
                            </button>
                        )}
                    </div>
                    <div className="md:col-span-2">
                        <label className={labelCls}>Closing message <span className="normal-case text-stone2">(optional — sent after a lead/case is captured, e.g. a thank-you)</span></label>
                        <input data-testid="wa-bot-closing" className={inputCls} value={cfg.closing_message || ""} onChange={(e) => set({ closing_message: e.target.value })} placeholder="Thanks for reaching out! Reply 'menu' anytime to start over." />
                    </div>
                    <div className="md:col-span-2">
                        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-stone-200 bg-stone-50/60 p-3.5">
                            <input type="checkbox" data-testid="wa-bot-reply-broadcasts" className="mt-0.5 h-4 w-4 accent-emerald-600"
                                checked={!!cfg.reply_to_broadcasts} onChange={(e) => set({ reply_to_broadcasts: e.target.checked })} />
                            <span className="text-sm">
                                <span className="font-semibold text-ink">Auto-reply to broadcast replies</span>
                                <span className="mt-0.5 block text-xs text-stone2">When off (recommended), the bot stays silent if someone replies to a broadcast — those land in the Inbox for an agent. Journey replies are always handled by the journey, and STOP/START opt-outs always work regardless.</span>
                            </span>
                        </label>
                    </div>
                    <div>
                        <label className={labelCls}>Menu title (header)</label>
                        <input data-testid="wa-bot-menu-header" className={inputCls} value={cfg.menu_header || ""} onChange={(e) => set({ menu_header: e.target.value })} maxLength={60} placeholder="How can we assist?" />
                    </div>
                    <div>
                        <label className={labelCls}>Menu button label</label>
                        <input data-testid="wa-bot-menu-button" className={inputCls} value={cfg.menu_button || ""} onChange={(e) => set({ menu_button: e.target.value })} maxLength={20} placeholder="Choose an option" />
                    </div>
                    <div className="md:col-span-2">
                        <label className={labelCls}>Menu body text</label>
                        <input data-testid="wa-bot-menu-body" className={inputCls} value={cfg.menu_body || ""} onChange={(e) => set({ menu_body: e.target.value })} placeholder="How can we assist you today?" />
                    </div>
                    <div className="md:col-span-2">
                        <label className={labelCls}>Trigger keywords <span className="normal-case text-stone2">(comma-separated — the bot also auto-starts on a customer's first message)</span></label>
                        <input data-testid="wa-bot-keywords" className={inputCls} value={(cfg.trigger_keywords || []).join(", ")} onChange={(e) => set({ trigger_keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="hi, hello, menu" />
                    </div>
                </div>
            </div>

            {/* Options */}
            <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2"><ListBullets size={18} className="text-emerald-600" /><h4 className="font-heading font-bold">Menu options ({cfg.options.length}/10)</h4></div>
                    <button data-testid="wa-bot-add-option" onClick={addOpt} disabled={cfg.options.length >= 10}
                        className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3.5 py-2 text-xs font-semibold text-stone2 transition-all duration-200 hover:border-ink hover:text-ink disabled:opacity-40">
                        <PlusCircle size={15} /> Add option
                    </button>
                </div>

                {cfg.options.length === 0 && <p className="text-sm text-stone2">No options yet. Add your first menu option (e.g. "New Solar Installation").</p>}

                <div className="space-y-4">
                    {cfg.options.map((o, oi) => {
                        return (
                            <div key={o._k} className="rounded-xl border border-stone-200 bg-stone-50/60 p-4" data-testid={`wa-bot-option-${oi}`}>
                                <div className="mb-3 flex items-start gap-3">
                                    <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">{oi + 1}</div>
                                    <input data-testid={`wa-bot-option-label-${oi}`} className={`${inputCls} font-semibold`} value={o.label} onChange={(e) => setOpt(oi, { label: e.target.value })} maxLength={24} placeholder="Option label (e.g. New Solar Installation)" />
                                    <div className="flex shrink-0 items-center gap-1">
                                        <button data-testid={`wa-bot-option-up-${oi}`} onClick={() => moveOpt(oi, -1)} className="rounded-lg border border-stone-300 p-1.5 text-stone2 hover:border-ink hover:text-ink"><ArrowUp size={13} /></button>
                                        <button data-testid={`wa-bot-option-down-${oi}`} onClick={() => moveOpt(oi, 1)} className="rounded-lg border border-stone-300 p-1.5 text-stone2 hover:border-ink hover:text-ink"><ArrowDown size={13} /></button>
                                        <button data-testid={`wa-bot-option-remove-${oi}`} onClick={() => removeOpt(oi)} className="rounded-lg border border-stone-300 p-1.5 text-stone2 hover:border-red-400 hover:text-red-500"><Trash size={13} /></button>
                                    </div>
                                </div>

                                <div className="mb-3 grid gap-3 md:grid-cols-2">
                                    <div>
                                        <label className={labelCls}>Description <span className="normal-case text-stone2">(optional)</span></label>
                                        <input data-testid={`wa-bot-option-desc-${oi}`} className={inputCls} value={o.description || ""} onChange={(e) => setOpt(oi, { description: e.target.value })} maxLength={72} placeholder="Short helper text" />
                                    </div>
                                    <div>
                                        <label className={labelCls}>When chosen</label>
                                        <select data-testid={`wa-bot-option-action-${oi}`} className={inputCls} value={o.action} onChange={(e) => setOpt(oi, { action: e.target.value })}>
                                            <option value="collect">Ask questions → capture a lead/case</option>
                                            <option value="submenu">Show a sub-menu (nested options)</option>
                                            <option value="escalate">Escalate to a human agent</option>
                                        </select>
                                    </div>
                                </div>

                                {o.action === "submenu" ? (
                                    <div className="space-y-3 rounded-lg border border-sky-200 bg-sky-50/40 p-3">
                                        <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-white px-3 py-2 text-[11px] text-sky-800" data-testid={`wa-bot-opt-${oi}-submenu-help`}>
                                            <Info size={14} className="mt-0.5 shrink-0" />
                                            <span>A sub-menu shows the customer <b>another set of buttons</b> instead of collecting details right away. Use it to branch — e.g. <b>{o.label || "Servicing Request"}</b> → <i>Periodic Service · Repair / Breakdown · Insurance Renewal</i>. Each sub-option below then asks its own questions or escalates to an agent.</span>
                                        </div>
                                        <div className="grid gap-3 md:grid-cols-3">
                                            <div>
                                                <label className={labelCls}>Sub-menu title</label>
                                                <input data-testid={`wa-bot-opt-${oi}-submenu-header`} className={inputCls} value={o.submenu_header || ""} onChange={(e) => setOpt(oi, { submenu_header: e.target.value })} maxLength={60} placeholder={o.label || "e.g. Servicing"} />
                                                <p className="mt-1 text-[10px] text-stone2">List header the customer sees</p>
                                            </div>
                                            <div>
                                                <label className={labelCls}>Sub-menu body</label>
                                                <input data-testid={`wa-bot-opt-${oi}-submenu-body`} className={inputCls} value={o.submenu_body || ""} onChange={(e) => setOpt(oi, { submenu_body: e.target.value })} placeholder="e.g. What do you need help with?" />
                                                <p className="mt-1 text-[10px] text-stone2">Message shown above the button</p>
                                            </div>
                                            <div>
                                                <label className={labelCls}>Sub-menu button</label>
                                                <input data-testid={`wa-bot-opt-${oi}-submenu-button`} className={inputCls} value={o.submenu_button || ""} onChange={(e) => setOpt(oi, { submenu_button: e.target.value })} maxLength={20} placeholder="e.g. Choose" />
                                                <p className="mt-1 text-[10px] text-stone2">Text on the tap-to-open button</p>
                                            </div>
                                        </div>
                                        {(o.children || []).some((c) => c.label) && (
                                            <div className="rounded-lg border border-dashed border-sky-300 bg-white px-3 py-2" data-testid={`wa-bot-opt-${oi}-submenu-preview`}>
                                                <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-stone2">Customer will tap one of:</div>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {(o.children || []).filter((c) => c.label).map((c, i) => (
                                                        <span key={i} className="rounded-full border border-sky-300 bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-sky-700">{c.label}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between">
                                            <label className={labelCls + " mb-0"}>Sub-options ({(o.children || []).length}/10) <span className="normal-case text-stone2">— the buttons shown after they tap “{o.label || "this"}”</span></label>
                                            <button data-testid={`wa-bot-opt-${oi}-add-child`} onClick={() => addChild(oi)} disabled={(o.children || []).length >= 10}
                                                className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-700 disabled:opacity-40"><PlusCircle size={14} /> Add sub-option</button>
                                        </div>
                                        {(o.children || []).length === 0 && (
                                            <p className="rounded-lg border border-dashed border-sky-300 bg-white px-3 py-2 text-xs text-stone2">
                                                No sub-options yet. Add the choices the customer can tap — e.g. <b>Periodic Service</b>, <b>Repair / Breakdown</b>, <b>Insurance Renewal</b>, <b>Talk to an advisor</b>. Each one then asks its own questions or escalates.
                                            </p>
                                        )}
                                        <div className="space-y-3 border-l-2 border-sky-200 pl-3">
                                            {(o.children || []).map((c, ci) => (
                                                <div key={c._k} className="rounded-lg border border-stone-200 bg-white p-3" data-testid={`wa-bot-child-${oi}-${ci}`}>
                                                    <div className="mb-2 flex items-center gap-2">
                                                        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-sky-100 text-[10px] font-bold text-sky-700">{oi + 1}.{ci + 1}</span>
                                                        <input data-testid={`wa-bot-child-label-${oi}-${ci}`} className={`${inputCls} font-semibold`} value={c.label} onChange={(e) => setChild(oi, ci, { label: e.target.value })} maxLength={24} placeholder="Sub-option label (e.g. Periodic Service)" />
                                                        <select data-testid={`wa-bot-child-action-${oi}-${ci}`} className={`${inputCls} w-44`} value={c.action} onChange={(e) => setChild(oi, ci, { action: e.target.value })}>
                                                            <option value="collect">Ask questions → capture</option>
                                                            <option value="escalate">Escalate to agent</option>
                                                        </select>
                                                        <button data-testid={`wa-bot-child-remove-${oi}-${ci}`} onClick={() => removeChild(oi, ci)} className="rounded-lg border border-stone-300 p-2 text-stone2 hover:border-red-400 hover:text-red-500"><Trash size={13} /></button>
                                                    </div>
                                                    <input data-testid={`wa-bot-child-desc-${oi}-${ci}`} className={`${inputCls} mb-2`} value={c.description || ""} onChange={(e) => setChild(oi, ci, { description: e.target.value })} maxLength={72} placeholder="Description shown under the button (optional)" />
                                                    <LeafEditor option={c} onOption={(patch) => setChild(oi, ci, patch)} onFields={(fs) => setChild(oi, ci, { fields: fs })}
                                                        objectOptions={objectOptions} fieldCache={fieldCache} loadFields={loadFields} p={`wa-bot-child-${oi}-${ci}`} inputCls={inputCls} labelCls={labelCls} sfConnected={sfOk} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <LeafEditor option={o} onOption={(patch) => setOpt(oi, patch)} onFields={(fs) => setOpt(oi, { fields: fs })}
                                        objectOptions={objectOptions} fieldCache={fieldCache} loadFields={loadFields} p={`wa-bot-opt-${oi}`} inputCls={inputCls} labelCls={labelCls} sfConnected={sfOk} />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex justify-end">
                <button data-testid="wa-bot-save" onClick={save} disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50">
                    {saving ? <CircleNotch size={16} className="animate-spin" /> : <CheckCircle size={16} weight="fill" />}
                    {saving ? "Saving…" : "Save bot flow"}
                </button>
            </div>
                </div>

                {/* Live WhatsApp preview */}
                <div className="lg:sticky lg:top-6 self-start">
                    <WhatsAppPreview cfg={cfg} />
                </div>
            </div>
            </>)}
        </div>
    );
}

function WhatsAppPreview({ cfg }) {
    const options = (cfg.options || []).filter((o) => o.label);
    return (
        <div data-testid="wa-bot-preview">
            <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-stone2">
                <Eye size={14} className="text-emerald-600" /> Live preview
            </div>
            <div className="overflow-hidden rounded-[2rem] border-4 border-stone-800 bg-stone-800 shadow-xl">
                {/* WA top bar */}
                <div className="flex items-center gap-2 bg-emerald-700 px-4 py-3 text-white">
                    <div className="grid h-8 w-8 place-items-center rounded-full bg-white/20"><WhatsappLogo size={18} weight="fill" /></div>
                    <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">Your Business</div>
                        <div className="text-[10px] text-emerald-100">online</div>
                    </div>
                </div>
                {/* chat area */}
                <div className="space-y-2 px-3 py-4" style={{ backgroundColor: "#ECE5DD", minHeight: 320 }}>
                    {cfg.welcome_image_ref && (
                        <div className="max-w-[80%] rounded-xl rounded-tl-sm bg-white p-1 shadow-sm">
                            <img src={`${API_BASE}/whatsapp/media/${cfg.welcome_image_ref}`} alt="welcome" className="rounded-lg" />
                        </div>
                    )}
                    {cfg.welcome_message && (
                        <div className="max-w-[80%] whitespace-pre-wrap break-words rounded-xl rounded-tl-sm bg-white px-3 py-2 text-[13px] text-stone-800 shadow-sm">
                            {cfg.welcome_message}
                        </div>
                    )}
                    {/* menu bubble */}
                    <div className="max-w-[85%] overflow-hidden rounded-xl rounded-tl-sm bg-white text-[13px] text-stone-800 shadow-sm">
                        <div className="px-3 pt-2.5">
                            {cfg.menu_header && <div className="font-bold">{cfg.menu_header}</div>}
                            {cfg.menu_body && <div className="mt-0.5 whitespace-pre-wrap break-words text-stone-600">{cfg.menu_body}</div>}
                        </div>
                        <div className="mt-2 border-t border-stone-100">
                            <div className="flex items-center justify-center gap-1.5 px-3 py-2.5 font-semibold text-sky-600">
                                <ListBullets size={16} /> {cfg.menu_button || "Choose an option"}
                            </div>
                        </div>
                    </div>
                    {/* faux list rows (what opens on tap) */}
                    {options.length > 0 && (
                        <div className="max-w-[92%] overflow-hidden rounded-xl bg-white shadow-sm">
                            <div className="border-b border-stone-100 px-3 py-2 text-xs font-bold uppercase tracking-wide text-stone-400">{cfg.menu_header || "Menu"}</div>
                            {options.map((o, i) => (
                                <div key={o._k || i} className="flex items-center justify-between border-b border-stone-50 px-3 py-2.5 last:border-0">
                                    <div className="min-w-0">
                                        <div className="truncate text-[13px] font-medium text-stone-800">{o.label}</div>
                                        {o.description && <div className="truncate text-[11px] text-stone-500">{o.description}</div>}
                                    </div>
                                    {o.action === "submenu" ? (
                                        <span className="ml-2 shrink-0 text-lg leading-none text-stone-400">›</span>
                                    ) : (
                                        <span className="ml-2 grid h-4 w-4 shrink-0 place-items-center rounded-full border border-stone-300 text-stone-300">
                                            <span className="h-2 w-2 rounded-full" />
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {options.length === 0 && (
                        <div className="max-w-[80%] rounded-xl bg-white/70 px-3 py-2 text-[12px] italic text-stone-500">Add menu options to preview them here.</div>
                    )}
                    {/* sub-menu screens: what the customer sees after tapping a sub-menu option */}
                    {options.filter((o) => o.action === "submenu" && (o.children || []).filter((c) => c.label).length > 0).map((o, i) => (
                        <div key={`sub-${o._k || i}`} className="max-w-[92%]" data-testid={`wa-bot-preview-submenu-${i}`}>
                            <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-stone-500">
                                <span className="text-stone-400">↳</span> After tapping “{o.label}”
                            </div>
                            <div className="overflow-hidden rounded-xl bg-white shadow-sm">
                                <div className="border-b border-stone-100 px-3 py-2 text-xs font-bold uppercase tracking-wide text-stone-400">{o.submenu_header || o.label}</div>
                                {(o.children || []).filter((c) => c.label).map((c, ci) => (
                                    <div key={c._k || ci} className="flex items-center justify-between border-b border-stone-50 px-3 py-2.5 last:border-0">
                                        <div className="min-w-0">
                                            <div className="truncate text-[13px] font-medium text-stone-800">{c.label}</div>
                                            {c.description && <div className="truncate text-[11px] text-stone-500">{c.description}</div>}
                                        </div>
                                        <span className="ml-2 grid h-4 w-4 shrink-0 place-items-center rounded-full border border-stone-300 text-stone-300"><span className="h-2 w-2 rounded-full" /></span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <p className="mt-2 text-center text-[11px] text-stone2">This is how the greeting, menu &amp; sub-menus appear on WhatsApp.</p>
        </div>
    );
}

export default function WhatsApp() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const tabs = isAdmin ? TABS_ADMIN : TABS_AGENT;
    const [searchParams, setSearchParams] = useSearchParams();
    const validKeys = tabs.map((t) => t.key);
    const urlTab = searchParams.get("tab");
    const tab = validKeys.includes(urlTab) ? urlTab : "inbox";
    const setTab = (v) => setSearchParams((p) => { p.set("tab", v); return p; }, { replace: true });

    return (
        <AppShell>
            <div className="pb-2">
                {/* Sticky page header */}
                <div className="sticky top-0 z-40 border-b border-stone-200 bg-paper/85 backdrop-blur-xl">
                    <div className="mx-auto max-w-7xl px-6 pt-5 md:px-12">
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600">
                            <WhatsappLogo size={15} weight="fill" /> Messaging Add-on
                        </div>
                        <h1 className="mt-1 font-heading text-2xl font-bold tracking-tight sm:text-3xl">WhatsApp</h1>
                        <p className="mt-1 text-sm text-stone2">Chat with customers on WhatsApp — from the app or Salesforce.</p>
                        <div className="mt-4 -mb-px flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                            {tabs.map(({ key, label, icon: Icon }) => {
                                const active = tab === key;
                                return (
                                    <button key={key} data-testid={`wa-tab-${key}`} onClick={() => setTab(key)}
                                        className={`inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.08em] transition-colors ${active ? "border-emerald-600 text-emerald-700" : "border-transparent text-stone2 hover:border-stone-300 hover:text-ink"}`}>
                                        <Icon size={16} weight={active ? "fill" : "regular"} className={active ? "text-emerald-600" : ""} /> {label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="mx-auto max-w-7xl px-6 py-6 md:px-12">
                    {tab === "inbox" && <Inbox />}
                    {tab === "broadcasts" && isAdmin && <WhatsAppBroadcasts />}
                    {tab === "journeys" && isAdmin && <WhatsAppJourneys />}
                    {tab === "templates" && <Templates />}
                    {tab === "bot" && isAdmin && <BotBuilder />}
                    {tab === "optouts" && isAdmin && <WhatsAppOptouts />}
                    {tab === "api" && isAdmin && <SalesforceApi />}
                </div>
            </div>
        </AppShell>
    );
}

